<template>
  <div :class="['model-schedule-page', { 'model-schedule-page--comparison': isComparisonWindow, 'model-schedule-page--profile': profileView }]">
    <div v-if="!isComparisonWindow && !profileView" class="schedule-page-heading">
      <div>
        <div class="schedule-eyebrow">CONTROL OPERATIVO</div>
        <h1>Model Schedule</h1>
        <p>Weekly studio planning for active models, shifts, and monitor coverage.</p>
      </div>
      <div class="schedule-page-actions">
        <v-chip v-if="studioId && !canEditSelectedStudio" size="small" color="grey" variant="tonal">
          View Only
        </v-chip>
        <span :class="['save-status', saveState]">{{ saveStatusText }}</span>
        <v-btn
          variant="outlined"
          prepend-icon="mdi-content-copy"
          :disabled="!schedule"
          :loading="isCopyingScreenshot"
          @click="copyScheduleScreenshot"
        >
          Screenshot
        </v-btn>
        <v-btn
          v-if="schedule && canEditSelectedStudio"
          :color="isEditing ? 'success' : 'primary'"
          :variant="isEditing ? 'flat' : 'outlined'"
          :prepend-icon="isEditing ? 'mdi-check' : 'mdi-pencil-outline'"
          @click="toggleEditMode"
        >
          {{ isEditing ? 'Done Editing' : 'Edit Schedule' }}
        </v-btn>
      </div>
    </div>

    <v-card class="schedule-controls" elevation="0">
      <div v-if="!profileView" class="schedule-studio-control">
        <span>Studio</span>
        <v-select
          v-model="studioId"
          :items="studios"
          item-title="name"
          item-value="id"
          density="compact"
          variant="outlined"
          hide-details
          :disabled="profileView"
        />
      </div>

      <div v-if="!profileView" class="schedule-name-control">
        <v-menu location="bottom start">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-eye-outline"
              size="small"
              variant="outlined"
              title="Choose how model names appear"
              aria-label="Choose how model names appear"
            />
          </template>
          <v-list density="compact" min-width="215">
            <v-list-subheader>DISPLAY MODEL NAMES</v-list-subheader>
            <v-list-item
              v-for="option in modelNameViewOptions"
              :key="option.value"
              :active="modelNameView === option.value"
              :prepend-icon="modelNameView === option.value ? 'mdi-check' : undefined"
              :title="option.label"
              @click="modelNameView = option.value"
            />
            <v-divider class="my-1" />
            <v-list-subheader>ROOM DISPLAY</v-list-subheader>
            <v-list-item
              :disabled="savingJokerSetting || !canEditSelectedStudio"
              :prepend-icon="jokerEnabled ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline'"
              title="Joker Row"
              :subtitle="jokerEnabled ? 'Shown for this studio' : 'Hidden for this studio'"
              @click="toggleJokerVisibility"
            />
          </v-list>
        </v-menu>
      </div>

      <div v-if="!profileView" class="schedule-compare-control">
        <v-btn
          :icon="isComparisonWindow ? 'mdi-window-close' : 'mdi-compare-horizontal'"
          size="small"
          variant="outlined"
          :disabled="!studioId"
          :title="isComparisonWindow ? 'Close comparison window' : 'Compare with another week'"
          :aria-label="isComparisonWindow ? 'Close comparison window' : 'Compare with another week'"
          @click="handleCompareClick"
        />
      </div>

      <div class="schedule-week-control">
        <v-btn icon="mdi-chevron-left" variant="text" density="comfortable" aria-label="Previous week" @click="moveWeek(-1)" />
        <div class="schedule-week-title">
          <span>{{ weekHeading }}</span>
        </div>
        <v-btn icon="mdi-chevron-right" variant="text" density="comfortable" aria-label="Next week" :disabled="profileView && !canMoveProfileWeekForward" @click="moveWeek(1)" />
      </div>

      <div class="schedule-controls-actions">
        <v-btn size="small" variant="text" @click="goToCurrentWeek">This Week</v-btn>
        <v-btn
          v-if="schedule && isEditing"
          size="small"
          variant="text"
          prepend-icon="mdi-undo"
          :disabled="!undoStack.length"
          title="Undo last schedule change (Ctrl+Z)"
          @click="undoLastChange"
        >
          Undo
        </v-btn>
        <v-btn v-if="isEditing" size="small" variant="text" prepend-icon="mdi-tune-variant" @click="openShiftDialog">Shift Settings</v-btn>
        <v-btn v-if="schedule && isEditing" size="small" variant="text" prepend-icon="mdi-content-save-outline" @click="saveAsBasePattern">
          Set as Base Pattern
        </v-btn>
        <v-btn
          v-if="schedule && isEditing"
          class="schedule-clear-week-btn"
          size="small"
          variant="text"
          prepend-icon="mdi-calendar-remove-outline"
          @click="requestClearWeek"
        >
          Clear Week
        </v-btn>
      </div>
    </v-card>

    <v-card v-if="!loading && isEditing" class="model-tray" elevation="0">
      <div class="model-tray__heading">
        <div>
          <strong>Model Tray</strong>
          <span>Select one or more models, then click an empty room cell to place them.</span>
          <span v-if="modelNameView === 'custom'">Edit custom model names here using the pencil beside each model.</span>
        </div>
        <div class="model-tray__actions">
          <v-chip v-if="hasPlacementSelection" size="small" color="primary" variant="tonal">
            {{ placementSelectionCount }} selected
          </v-chip>
          <v-btn v-if="hasPlacementSelection" size="small" variant="text" @click="clearModelSelection">Clear</v-btn>
          <v-btn size="small" variant="outlined" prepend-icon="mdi-account-plus-outline" @click="openGuestModelDialog">
            Add Guest
          </v-btn>
        </div>
      </div>
      <div class="model-tray__body">
        <v-text-field
          v-model="modelSearch"
          class="model-tray__search"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          variant="outlined"
          hide-details
          placeholder="Find a model"
        />
        <div class="model-tray__models">
          <button
            v-for="model in filteredModels"
            :key="model.id"
            :class="['model-tray-chip', { 'model-tray-chip--selected': isModelSelected(model.id) }]"
            type="button"
            @click="toggleModelSelection(model.id)"
          >
            <span>{{ displayModel(model.id) }}</span>
            <v-icon
              v-if="modelNameView === 'custom'"
              class="model-tray-chip__edit"
              size="14"
              title="Edit custom schedule name"
              role="button"
              tabindex="0"
              @click.stop="openCustomNameDialog(model.id)"
              @keydown.enter.stop.prevent="openCustomNameDialog(model.id)"
            >
              mdi-pencil-outline
            </v-icon>
          </button>
          <button
            v-for="guestName in guestNamesForWeek"
            :key="`guest-${guestName}`"
            :class="['model-tray-chip', 'model-tray-chip--guest', { 'model-tray-chip--selected': selectedGuestName === guestName }]"
            type="button"
            @click="toggleGuestSelection(guestName)"
          >
            <v-icon size="13">mdi-account-star-outline</v-icon>
            <span>{{ guestName }}</span>
          </button>
        </div>
      </div>
    </v-card>

    <v-alert v-if="error" type="warning" variant="tonal" class="mb-4" closable @click:close="error = ''">
      {{ error }}
    </v-alert>

    <div v-if="loading" class="schedule-loading">
      <v-progress-circular indeterminate color="primary" />
      <span>Loading schedule...</span>
    </div>

    <v-card v-else-if="!schedule" class="schedule-empty" elevation="0">
      <div class="schedule-empty__icon"><v-icon size="32">mdi-calendar-blank-outline</v-icon></div>
      <h2>No schedule for this week</h2>
      <p v-if="isComparisonWindow">There is no saved schedule for this studio and week.</p>
      <template v-else-if="canEditSelectedStudio">
        <p>Create one from your recurring base pattern, copy the prior week, or begin with an empty grid.</p>
        <v-btn color="primary" prepend-icon="mdi-plus" @click="openNewWeekDialog">Create {{ weekHeading }}</v-btn>
      </template>
      <p v-else>This studio has not created a schedule for this week.</p>
    </v-card>

    <template v-else>
      <div ref="scheduleScreenshot" :class="['schedule-screenshot-capture', { 'is-capturing': isScreenshotting }]">
      <div v-if="isScreenshotting" class="schedule-screenshot-heading">
        {{ weekHeading }}
      </div>
      <div class="schedule-grid-shell">
        <section v-for="shift in visibleShiftTemplates" :key="shift.id" class="schedule-shift-section">
          <div class="schedule-shift-heading" :style="{ backgroundColor: shiftHeaderColor(shift) }">
            <div class="schedule-shift-heading__name">
              <strong>{{ shift.name }}</strong>
            </div>
            <div
              v-for="day in weekDays"
              :key="`event-${shift.id}-${day.index}`"
              class="schedule-shift-event-cell"
            >
              <v-text-field
                v-if="isEditing && !isScreenshotting"
                v-model="eventNoteValues[monitorNoteKey(shift.id, day.iso)]"
                density="compact"
                variant="plain"
                hide-details
                placeholder="+ Event"
                @focus="startFieldEditUndo"
                @blur="finishFieldEditUndo"
                @update:model-value="queueSave"
              />
              <span v-else class="schedule-shift-event-value">
                {{ eventNoteValues[monitorNoteKey(shift.id, day.iso)] || '' }}
              </span>
            </div>
          </div>

          <div class="schedule-grid">
            <div class="schedule-corner">
              <small class="schedule-corner-time">
                {{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }}
              </small>
            </div>
            <div v-for="day in weekDays" :key="`head-${shift.id}-${day.index}`" class="schedule-day-heading">
              <span>{{ day.short }}</span>
              <strong>{{ day.dateNumber }}</strong>
            </div>

            <div class="schedule-monitor-label">Monitor</div>
            <div v-for="day in weekDays" :key="`monitor-${shift.id}-${day.index}`" class="schedule-monitor-cell">
              <v-text-field
                v-if="isEditing"
                v-model="monitorNoteValues[monitorNoteKey(shift.id, day.iso)]"
                density="compact"
                variant="plain"
                hide-details
                placeholder="Add monitor"
                @focus="startFieldEditUndo"
                @blur="finishFieldEditUndo"
                @update:model-value="queueSave"
              />
              <span v-else class="schedule-monitor-value">
                {{ monitorNoteValues[monitorNoteKey(shift.id, day.iso)] || '—' }}
              </span>
            </div>

            <template v-for="room in displayedRoomSlots(shift.id)" :key="`room-${shift.id}-${room.slot}`">
              <div class="schedule-row-label schedule-room-label">
                <span>{{ room.slot === 7 ? 'Joker' : 'Room' }}</span>
                <strong v-if="room.slot !== 7">{{ room.label }}</strong>
              </div>
              <div
                v-for="day in weekDays"
                :key="`cell-${shift.id}-${room.slot}-${day.index}`"
                :class="['schedule-model-cell', 'schedule-room-cell', {
                  'schedule-model-cell--editing': isEditing,
                  'schedule-model-cell--ready': isEditing && hasPlacementSelection,
                  'schedule-model-cell--drag-target': isDragTarget(shift.id, day.index, room.slot),
                }]"
                @click="handleScheduleCellClick(shift.id, day.index, room.slot)"
                @dragenter.prevent="setDragTarget(shift.id, day.index, room.slot)"
                @dragover.prevent
                @dragleave="clearDragTarget(shift.id, day.index, room.slot)"
                @drop.stop.prevent="moveDraggedAssignment(shift.id, day.index, room.slot)"
              >
                <template v-if="roomEntry(shift.id, day.index, room.slot)">
                  <v-menu v-if="isEditing" location="bottom end">
                    <template #activator="{ props }">
                      <div
                        v-bind="props"
                        :class="['schedule-model-chip', 'schedule-model-chip--editable', statusChipClass(roomEntry(shift.id, day.index, room.slot).assignment_type)]"
                        :style="isModelAssignment(roomEntry(shift.id, day.index, room.slot))
                          ? { color: entryTextColor(roomEntry(shift.id, day.index, room.slot)) }
                          : undefined"
                        :draggable="isModelAssignment(roomEntry(shift.id, day.index, room.slot))"
                        :title="isModelAssignment(roomEntry(shift.id, day.index, room.slot))
                          ? 'Drag to move, or click to edit'
                          : 'Edit this assignment'"
                        @click.stop
                        @dragstart.stop="startAssignmentDrag($event, shift.id, day.index, room.slot)"
                        @dragend="endAssignmentDrag"
                      >
                        <span class="schedule-model-chip__mainline">
                          <span class="schedule-model-chip__label">
                            {{ entryLabel(roomEntry(shift.id, day.index, room.slot)) }}
                          </span>
                          <span
                            v-if="roomEntry(shift.id, day.index, room.slot).is_model_focus"
                            class="schedule-model-focus-marker"
                            title="Model Focus"
                          >
                            <v-icon size="15">mdi-target</v-icon>
                            Focus
                          </span>
                          <span
                            v-if="entryWorkTime(roomEntry(shift.id, day.index, room.slot))"
                            class="schedule-model-until"
                            :title="entryWorkTimeTitle(roomEntry(shift.id, day.index, room.slot))"
                          >
                            <v-icon size="13">mdi-clock-outline</v-icon>
                            {{ entryWorkTime(roomEntry(shift.id, day.index, room.slot)) }}
                          </span>
                        </span>
                        <v-icon class="schedule-model-chip__edit-icon" size="13">mdi-pencil-outline</v-icon>
                      </div>
                    </template>
                    <v-card class="schedule-cell-menu" elevation="6" min-width="270">
                      <v-list density="compact">
                        <template v-if="isModelAssignment(roomEntry(shift.id, day.index, room.slot))">
                          <v-list-subheader>TEXT COLOR</v-list-subheader>
                          <div class="schedule-color-palette">
                            <button
                              v-for="color in textColorOptions"
                              :key="color.value"
                              type="button"
                              :class="['schedule-color-swatch', { 'schedule-color-swatch--active': entryTextColor(roomEntry(shift.id, day.index, room.slot)) === color.value }]"
                              :style="{ backgroundColor: color.value }"
                              :title="color.label"
                              :aria-label="`${color.label} text`"
                              @click="setEntryTextColor(roomEntry(shift.id, day.index, room.slot), color.value)"
                            />
                          </div>
                          <v-divider />
                          <div class="schedule-quick-actions">
                            <button
                              type="button"
                              class="schedule-quick-action"
                              title="Copy to next day"
                              @click="copyAssignmentToNextDay(roomEntry(shift.id, day.index, room.slot), shift.id, room.slot, day.index)"
                            >
                              <v-icon size="19">mdi-arrow-right</v-icon>
                              <span>Next</span>
                            </button>
                            <button
                              type="button"
                              class="schedule-quick-action"
                              title="Fill this room across the week"
                              @click="copyAssignmentAcrossWeek(roomEntry(shift.id, day.index, room.slot), shift.id, room.slot)"
                            >
                              <v-icon size="19">mdi-chevron-double-right</v-icon>
                              <span>Fill</span>
                            </button>
                            <button
                              type="button"
                              :class="['schedule-quick-action', { 'schedule-quick-action--active': entryWorkTime(roomEntry(shift.id, day.index, room.slot)) }]"
                              :title="entryWorkTime(roomEntry(shift.id, day.index, room.slot)) ? 'Change working time' : 'Add working time'"
                              @click="openWorkUntilDialog(roomEntry(shift.id, day.index, room.slot))"
                            >
                              <v-icon size="19">mdi-clock-outline</v-icon>
                              <span>Time</span>
                            </button>
                            <button
                              type="button"
                              :class="['schedule-quick-action', { 'schedule-quick-action--active': roomEntry(shift.id, day.index, room.slot).note }]"
                              :title="roomEntry(shift.id, day.index, room.slot).note ? 'Edit note' : 'Add note'"
                              @click="openEntryNoteDialog(roomEntry(shift.id, day.index, room.slot))"
                            >
                              <v-icon size="19">mdi-note-text-outline</v-icon>
                              <span>Note</span>
                            </button>
                          </div>
                        </template>
                        <v-divider />
                        <v-list-subheader>SET REASON</v-list-subheader>
                        <v-list-item
                          v-if="isModelAssignment(roomEntry(shift.id, day.index, room.slot))"
                          title="Model Focus"
                          prepend-icon="mdi-target"
                          :active="Boolean(roomEntry(shift.id, day.index, room.slot).is_model_focus)"
                          active-color="primary"
                          @click="toggleModelFocus(roomEntry(shift.id, day.index, room.slot))"
                        />
                        <v-list-item
                          v-for="reason in reasonOptions"
                          :key="reason.value"
                          :title="reason.label"
                          @click="applyReasonToCell(reason.value, shift.id, day.index, room.slot)"
                        />
                        <v-divider />
                        <v-list-subheader class="schedule-delete-heading">DELETE</v-list-subheader>
                        <div class="schedule-delete-actions">
                          <button
                            type="button"
                            class="schedule-delete-action"
                            title="Delete this cell"
                            @click="removeRoomEntry(shift.id, day.index, room.slot)"
                          >
                            <v-icon size="19">mdi-delete-outline</v-icon>
                            <span>Cell</span>
                          </button>
                          <button
                            type="button"
                            class="schedule-delete-action"
                            title="Delete this room row across the shift"
                            @click="requestClearRow(shift.id, room.slot)"
                          >
                            <v-icon size="19">mdi-table-row-remove</v-icon>
                            <span>Row</span>
                          </button>
                          <button
                            type="button"
                            class="schedule-delete-action"
                            title="Delete this day column within the shift"
                            @click="clearShiftDayColumn(shift.id, day.index)"
                          >
                            <v-icon size="19">mdi-table-column-remove</v-icon>
                            <span>Column</span>
                          </button>
                          <button
                            type="button"
                            class="schedule-delete-action"
                            title="Delete every cell in this shift"
                            @click="requestClearShift(shift.id)"
                          >
                            <v-icon size="19">mdi-table-large-remove</v-icon>
                            <span>Shift</span>
                          </button>
                        </div>
                      </v-list>
                    </v-card>
                  </v-menu>
                  <div
                    v-else
                    :class="isModelAssignment(roomEntry(shift.id, day.index, room.slot))
                      ? 'schedule-model-text'
                      : ['schedule-model-chip', 'schedule-model-reason', statusChipClass(roomEntry(shift.id, day.index, room.slot).assignment_type)]"
                    :style="isModelAssignment(roomEntry(shift.id, day.index, room.slot))
                      ? { color: entryTextColor(roomEntry(shift.id, day.index, room.slot)) }
                      : undefined"
                  >
                    <template v-if="isModelAssignment(roomEntry(shift.id, day.index, room.slot))">
                      <span class="schedule-model-display-mainline">
                        <span class="schedule-model-display-name">
                          {{ entryLabel(roomEntry(shift.id, day.index, room.slot)) }}
                        </span>
                        <span
                          v-if="roomEntry(shift.id, day.index, room.slot).is_model_focus"
                          class="schedule-model-focus-marker schedule-model-focus-marker--display"
                          title="Model Focus"
                          >
                            <v-icon size="15">mdi-target</v-icon>
                            Focus
                          </span>
                        <span
                          v-if="entryWorkTime(roomEntry(shift.id, day.index, room.slot))"
                          class="schedule-model-until schedule-model-until--display"
                          :title="entryWorkTimeTitle(roomEntry(shift.id, day.index, room.slot))"
                        >
                          <v-icon size="13">mdi-clock-outline</v-icon>
                          {{ entryWorkTime(roomEntry(shift.id, day.index, room.slot)) }}
                        </span>
                      </span>
                    </template>
                    <span v-else class="schedule-model-chip__label">
                      {{ entryLabel(roomEntry(shift.id, day.index, room.slot)) }}
                    </span>
                  </div>
                </template>
                <button
                  v-if="roomEntry(shift.id, day.index, room.slot)?.note"
                  type="button"
                  class="schedule-cell-note-marker"
                  aria-label="View schedule note"
                  :data-comment="roomEntry(shift.id, day.index, room.slot).note"
                  @click.stop="openEntryNoteDialog(roomEntry(shift.id, day.index, room.slot))"
                />
                <div v-if="isEditing && !roomEntry(shift.id, day.index, room.slot)" class="schedule-empty-cell" @click.stop>
                  <button
                    v-if="hasPlacementSelection"
                    type="button"
                    class="schedule-apply-hint"
                    @click.stop="applySelectedModelsToShift(shift.id, day.index, room.slot)"
                  >
                    <v-icon size="15">mdi-cursor-default-click-outline</v-icon>
                    Click to place
                  </button>
                  <v-autocomplete
                    v-if="!hasPlacementSelection"
                    v-model="cellSelections[cellSelectionKey(shift.id, day.index, room.slot)]"
                    :items="availableModelsForDay(day.index, shift.id)"
                    :item-title="modelDisplayName"
                    item-value="id"
                    density="compact"
                    variant="plain"
                    hide-details
                    placeholder="+ Add model"
                    no-data-text="All active models assigned"
                    @update:model-value="addModelToShift($event, shift.id, day.index, room.slot)"
                  />
                  <v-menu location="bottom end">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-dots-vertical"
                        size="x-small"
                        variant="text"
                        aria-label="Add a reason"
                      />
                    </template>
                    <v-list density="compact" min-width="160">
                      <v-list-subheader>ADD REASON</v-list-subheader>
                      <v-list-item
                        v-for="reason in reasonOptions"
                        :key="reason.value"
                        :title="reason.label"
                        @click="applyReasonToCell(reason.value, shift.id, day.index, room.slot)"
                      />
                      <v-divider />
                      <v-list-item
                        class="schedule-cell-menu__remove"
                        prepend-icon="mdi-table-row-remove"
                        title="Clear this room row"
                        @click="requestClearRow(shift.id, room.slot)"
                      />
                      <v-list-item
                        class="schedule-cell-menu__remove"
                        prepend-icon="mdi-table-column-remove"
                        title="Clear this day column"
                        @click="clearShiftDayColumn(shift.id, day.index)"
                      />
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </template>
          </div>
        </section>
      </div>
      </div>

    </template>

    <v-dialog v-model="newWeekDialog" max-width="520">
      <v-card class="schedule-dialog-card">
        <v-card-title>Create Weekly Schedule</v-card-title>
        <v-card-text>
          <p class="schedule-dialog-helper">{{ studioName }} · {{ weekHeading }}</p>
          <v-radio-group v-model="creationMode" hide-details class="schedule-creation-options">
            <v-radio value="copy">
              <template #label>
                <div><strong>Copy Previous Week</strong><small>Bring over the last saved schedule and edit the exceptions.</small></div>
              </template>
            </v-radio>
            <v-radio value="base">
              <template #label>
                <div><strong>Apply Base Pattern</strong><small>Build this week from active models and this studio's saved pattern.</small></div>
              </template>
            </v-radio>
            <v-radio value="blank">
              <template #label>
                <div><strong>Blank Week</strong><small>Start with an empty grid and place active models manually.</small></div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="newWeekDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="creating" @click="createSchedule">Create Schedule</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="shiftDialog" max-width="680">
      <v-card class="schedule-dialog-card">
        <v-card-title>Shift Settings</v-card-title>
        <v-card-text>
          <p class="schedule-dialog-helper">These shift names and times are used for every {{ studioName }} schedule.</p>
          <div v-for="template in editableShiftTemplates" :key="template.id" class="shift-settings-row">
            <v-text-field v-model="template.name" label="Shift" density="compact" variant="outlined" hide-details />
            <v-text-field v-model="template.start_time" label="Start" type="time" density="compact" variant="outlined" hide-details />
            <v-text-field v-model="template.end_time" label="Finish" type="time" density="compact" variant="outlined" hide-details />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="shiftDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="savingShifts" @click="saveShiftTemplates">Save Shifts</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="workUntilDialog" max-width="390">
      <v-card class="schedule-dialog-card">
        <v-card-title>Extended Shift</v-card-title>
        <v-card-text>
          <p class="schedule-dialog-helper">Add a start time, an end time, or both for this day.</p>
          <div class="schedule-work-time-fields">
            <v-text-field
              v-model="workFromDraft"
              label="Start time"
              type="time"
              density="compact"
              variant="outlined"
              autofocus
              hide-details
            />
            <v-text-field
              v-model="workUntilDraft"
              label="End time"
              type="time"
              density="compact"
              variant="outlined"
              hide-details
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="workUntilEntry && (workUntilEntry.work_from || workUntilEntry.work_until)" color="error" variant="text" @click="clearWorkUntil">Remove Time</v-btn>
          <v-spacer />
          <v-btn variant="text" @click="closeWorkUntilDialog">Cancel</v-btn>
          <v-btn color="primary" :disabled="!workFromDraft && !workUntilDraft" @click="saveWorkUntil">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="entryNoteDialog" max-width="520">
      <v-card class="schedule-dialog-card">
        <v-card-title>Schedule Note</v-card-title>
        <v-card-text>
          <p class="schedule-dialog-helper">{{ entryNoteLabel }}</p>
          <v-textarea
            v-if="isEditing"
            v-model="entryNoteDraft"
            label="Note"
            rows="3"
            variant="outlined"
            maxlength="2000"
            counter
            autofocus
            hide-details="auto"
          />
          <div v-else class="schedule-note-readonly">{{ entryNoteDraft }}</div>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="isEditing && entryNoteEntry?.note" color="error" variant="text" @click="clearEntryNote">Delete</v-btn>
          <v-spacer />
          <v-btn variant="text" @click="closeEntryNoteDialog">{{ isEditing ? 'Cancel' : 'Close' }}</v-btn>
          <v-btn v-if="isEditing" color="primary" :disabled="!entryNoteDraft.trim()" @click="saveEntryNote">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="customNameDialog" max-width="430">
      <v-card class="schedule-dialog-card">
        <v-card-title>Custom Schedule Name</v-card-title>
        <v-card-text>
          <p class="schedule-dialog-helper">
            {{ customNameModel?.stageName || customNameModel?.fullName }} · Only changes how this model appears on schedules.
          </p>
          <v-text-field
            v-model="customNameDraft"
            label="Custom name"
            placeholder="Preferred name or nickname"
            maxlength="80"
            counter
            autofocus
            variant="outlined"
            hide-details="auto"
            @keydown.enter.prevent="saveCustomName"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="customNameModel?.scheduleDisplayName"
            color="error"
            variant="text"
            :disabled="savingCustomName"
            @click="clearCustomName"
          >
            Use First Name
          </v-btn>
          <v-spacer />
          <v-btn variant="text" :disabled="savingCustomName" @click="closeCustomNameDialog">Cancel</v-btn>
          <v-btn color="primary" :loading="savingCustomName" @click="saveCustomName">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="guestModelDialog" max-width="430">
      <v-card class="schedule-dialog-card">
        <v-card-title>Add Guest</v-card-title>
        <v-card-text>
          <p class="schedule-dialog-helper">This temporary name belongs only to {{ weekHeading }} and will not create a model profile.</p>
          <v-text-field
            v-model="guestNameDraft"
            label="Guest model name"
            placeholder="Name to show on the schedule"
            maxlength="80"
            counter
            autofocus
            variant="outlined"
            hide-details="auto"
            @keydown.enter.prevent="confirmGuestModel"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeGuestModelDialog">Cancel</v-btn>
          <v-btn color="primary" :disabled="!guestNameDraft.trim()" @click="confirmGuestModel">Add &amp; Select</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="clearDialog" max-width="470">
      <v-card class="schedule-dialog-card">
        <v-card-title>{{ clearDialogTitle }}</v-card-title>
        <v-card-text>
          <p class="schedule-dialog-helper">{{ clearDialogMessage }}</p>
          <strong>This cannot be undone after it saves.</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cancelClear">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="confirmClear">Clear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { addDays, addWeeks, format, parseISO, startOfWeek } from 'date-fns';
import { toast } from 'vue3-toastify';
import secureAxios from '@/utils/secureAxios';

export default {
  props: {
    profileView: { type: Boolean, default: false },
    modelId: { type: Number, default: null },
  },
  data() {
    const today = new Date();
    return {
      studios: [],
      studioId: null,
      shiftTemplates: [],
      models: [],
      monitors: [],
      basePatterns: [],
      schedule: null,
      weekStart: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      maxProfileWeekStart: null,
      monitorNoteValues: {},
      eventNoteValues: {},
      cellSelections: {},
      selectedModelIds: [],
      selectedGuestName: '',
      modelSearch: '',
      modelNameView: localStorage.getItem('modelScheduleNameView') || 'stage_name',
      jokerEnabled: true,
      savingJokerSetting: false,
      modelNameViewOptions: [
        { label: 'Stage Name', value: 'stage_name' },
        { label: 'First Name', value: 'first_name' },
        { label: 'First Name + Initial', value: 'first_initial' },
        { label: 'First Name + Last Name', value: 'first_last' },
        { label: 'Custom Name', value: 'custom' },
      ],
      reasonOptions: [
        { label: 'Day Off', value: 'day_off' },
        { label: 'Vacation', value: 'vacation' },
        { label: 'Permission', value: 'permission' },
        { label: 'Period Day', value: 'period_day' },
        { label: 'Changed Shift', value: 'changed_shift' },
      ],
      textColorOptions: [
        { label: 'Black', value: '#202020' },
        { label: 'Blue', value: '#2C74B3' },
        { label: 'Green', value: '#2E7D32' },
        { label: 'Orange', value: '#D96B16' },
        { label: 'Pink', value: '#C23B72' },
        { label: 'Purple', value: '#7B4AB5' },
      ],
      loading: true,
      creating: false,
      newWeekDialog: false,
      creationMode: 'copy',
      shiftDialog: false,
      workUntilDialog: false,
      workUntilEntry: null,
      workFromDraft: '',
      workUntilDraft: '',
      entryNoteDialog: false,
      entryNoteEntry: null,
      entryNoteDraft: '',
      customNameDialog: false,
      customNameModel: null,
      customNameDraft: '',
      savingCustomName: false,
      guestModelDialog: false,
      guestNameDraft: '',
      editableShiftTemplates: [],
      savingShifts: false,
      isEditing: false,
      saveState: 'saved',
      saveTimer: null,
      editRevision: 0,
      saveInFlight: false,
      saveRequestedWhileInFlight: false,
      loadRequestId: 0,
      isScreenshotting: false,
      isCopyingScreenshot: false,
      draggedAssignment: null,
      dragTargetKey: null,
      clearDialog: false,
      clearRequest: null,
      undoStack: [],
      fieldEditUndoSnapshot: null,
      error: '',
    };
  },
  computed: {
    isComparisonWindow() {
      return this.$route.query.compare === '1';
    },
    authenticatedUser() {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}');
      } catch {
        return {};
      }
    },
    isAdminAccount() {
      return this.authenticatedUser?.role === 'admin' || this.authenticatedUser?.isAdmin === true;
    },
    canEditSelectedStudio() {
      if (this.profileView) return false;
      if (this.authenticatedUser?.role === 'hr') return false;
      return this.isAdminAccount
        || Number(this.authenticatedUser?.studio_id) === Number(this.studioId);
    },
    entryNoteLabel() {
      if (!this.entryNoteEntry) return '';
      const day = this.weekDays.find((item) => Number(item.index) === Number(this.entryNoteEntry.day_of_week));
      const shift = this.shiftTemplates.find((item) => Number(item.id) === Number(this.entryNoteEntry.shift_template_id));
      const room = Number(this.entryNoteEntry.room_slot) === 7 ? 'Joker' : `Room ${this.entryNoteEntry.room_slot}`;
      const dateLabel = day ? `${day.short} ${day.dateNumber}` : '';
      return [this.entryLabel(this.entryNoteEntry), dateLabel, shift?.name, room].filter(Boolean).join(' · ');
    },
    studioName() {
      return this.studios.find((studio) => Number(studio.id) === Number(this.studioId))?.name || 'Studio';
    },
    weekDays() {
      const start = parseISO(this.weekStart);
      return Array.from({ length: 7 }, (_, index) => {
        const date = addDays(start, index);
        return {
          index,
          iso: format(date, 'yyyy-MM-dd'),
          short: format(date, 'EEE').toUpperCase(),
          dateNumber: format(date, 'd'),
        };
      });
    },
    weekHeading() {
      const start = parseISO(this.weekStart);
      const end = addDays(start, 6);
      return `${format(start, 'MMMM d')} - ${format(end, 'MMMM d, yyyy')}`;
    },
    roomSlots() {
      const rooms = [
        { slot: 1, label: '1' },
        { slot: 2, label: '2' },
        { slot: 3, label: '3' },
        { slot: 4, label: '4' },
        { slot: 5, label: '5' },
        { slot: 6, label: '6' },
        { slot: 7, label: 'Joker' },
      ];
      return this.jokerEnabled ? rooms : rooms.filter((room) => room.slot !== 7);
    },
    saveStatusText() {
      if (!this.schedule) return '';
      if (this.saveState === 'saving') return 'Saving...';
      if (this.saveState === 'unsaved') return 'Changes pending';
      if (this.saveState === 'error') return 'Save failed';
      return 'Saved';
    },
    clearDialogTitle() {
      return this.clearRequest?.type === 'shift' ? 'Clear Entire Shift?' : 'Clear Entire Week?';
    },
    clearDialogMessage() {
      if (this.clearRequest?.type === 'shift') {
        const shift = this.shiftTemplates.find((item) => Number(item.id) === Number(this.clearRequest.shiftId));
        return `Remove every model assignment and reason from the ${shift?.name || 'selected'} shift for ${this.weekHeading}? You can undo this while the schedule remains open.`;
      }
      return `Remove this entire schedule for ${this.weekHeading}? The creation choices will become available again afterward.`;
    },
    filteredModels() {
      const search = this.modelSearch.trim().toLowerCase();
      if (!search) return this.models;
      return this.models.filter((model) => (
        String(model.stageName || '').toLowerCase().includes(search)
        || String(model.fullName || '').toLowerCase().includes(search)
        || String(model.scheduleDisplayName || '').toLowerCase().includes(search)
      ));
    },
    guestNamesForWeek() {
      const names = (this.schedule?.entries || [])
        .filter((entry) => entry.assignment_type === 'guest')
        .map((entry) => String(entry.custom_name || '').trim())
        .filter(Boolean);
      if (this.selectedGuestName) names.push(this.selectedGuestName);
      return [...new Set(names)].sort((a, b) => a.localeCompare(b));
    },
    hasPlacementSelection() {
      return !!this.selectedGuestName || this.selectedModelIds.length > 0;
    },
    placementSelectionCount() {
      return this.selectedGuestName ? 1 : this.selectedModelIds.length;
    },
    visibleShiftTemplates() {
      if (!this.isScreenshotting) return this.shiftTemplates;
      return this.shiftTemplates.filter((shift) => this.displayedRoomSlots(shift.id).length);
    },
    canMoveProfileWeekForward() {
      return !this.maxProfileWeekStart || this.weekStart < this.maxProfileWeekStart;
    },
  },
  watch: {
    studioId() {
      if (this.studioId) {
        this.syncComparisonRoute();
        this.loadStudioSchedule();
      }
    },
    modelNameView(value) {
      localStorage.setItem('modelScheduleNameView', value);
    },
  },
  async mounted() {
    window.addEventListener('keydown', this.handleUndoShortcut);
    if (this.isComparisonWindow) {
      const requestedWeek = String(this.$route.query.week_start || '');
      const parsedWeek = parseISO(requestedWeek);
      if (/^\d{4}-\d{2}-\d{2}$/.test(requestedWeek)
        && !Number.isNaN(parsedWeek.getTime())
        && format(startOfWeek(parsedWeek, { weekStartsOn: 1 }), 'yyyy-MM-dd') === requestedWeek) {
        this.weekStart = requestedWeek;
      }
      document.title = 'Schedule Comparison · Modato';
    }
    await this.loadStudios();
  },
  beforeUnmount() {
    clearTimeout(this.saveTimer);
    window.removeEventListener('keydown', this.handleUndoShortcut);
  },
  methods: {
    async loadStudios() {
      try {
        if (this.profileView && Number(this.modelId) > 0) {
          const { data } = await secureAxios.get(`/api/model-schedule/model/${Number(this.modelId)}/context`);
          this.studios = data.studio ? [data.studio] : [];
          this.maxProfileWeekStart = data.max_week_start || this.weekStart;
          this.shiftTemplates = data.shift_templates || [];
          this.models = data.models || [];
          this.jokerEnabled = data.joker_enabled !== false;
          this.studioId = data.studio?.id || null;
          return;
        }
        const response = await secureAxios.get('/api/model-schedule/studios');
        this.studios = response.data || [];
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const defaultStudio = this.studios.find((studio) => Number(studio.id) === Number(user.studio_id));
        const requestedStudio = this.isComparisonWindow
          ? this.studios.find((studio) => Number(studio.id) === Number(this.$route.query.studio_id))
          : null;
        this.studioId = requestedStudio?.id || defaultStudio?.id || this.studios[0]?.id || null;
      } catch (error) {
        this.error = 'Unable to load studios.';
      }
    },
    async loadStudioSchedule() {
      const requestId = ++this.loadRequestId;
      const requestedStudioId = this.studioId;
      const requestedWeekStart = this.weekStart;
      this.loading = true;
      this.error = '';
      clearTimeout(this.saveTimer);
      this.isEditing = false;
      this.selectedModelIds = [];
      this.selectedGuestName = '';
      try {
        if (this.profileView) {
          const scheduleResponse = await secureAxios.get(
            `/api/model-schedule/model/${Number(this.modelId)}/week`,
            { params: { week_start: this.weekStart } }
          );
          if (requestId !== this.loadRequestId
            || Number(requestedStudioId) !== Number(this.studioId)
            || requestedWeekStart !== this.weekStart) return;
          if (scheduleResponse.data?.max_week_start) this.maxProfileWeekStart = scheduleResponse.data.max_week_start;
          if (!this.models.length && scheduleResponse.data?.model) {
            this.models = [scheduleResponse.data.model];
          }
          this.monitors = [];
          this.basePatterns = [];
          this.applySchedule(scheduleResponse.data?.schedule);
          return;
        }
        const [contextResponse, scheduleResponse] = await Promise.all([
          secureAxios.get('/api/model-schedule/context', { params: { studio_id: this.studioId } }),
          secureAxios.get('/api/model-schedule/week', { params: { studio_id: this.studioId, week_start: this.weekStart } }),
        ]);
        if (requestId !== this.loadRequestId
          || Number(requestedStudioId) !== Number(this.studioId)
          || requestedWeekStart !== this.weekStart) return;
        const context = contextResponse.data || {};
        this.shiftTemplates = context.shift_templates || [];
        this.models = context.models || [];
        this.monitors = context.monitors || [];
        this.basePatterns = context.base_patterns || [];
        this.jokerEnabled = context.joker_enabled !== false;
        this.applySchedule(scheduleResponse.data);
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        console.error('Unable to load model schedule:', error?.message || error);
        this.error = error?.response?.data?.message || 'Unable to load this weekly schedule.';
        this.schedule = null;
      } finally {
        if (requestId === this.loadRequestId) this.loading = false;
      }
    },
    applySchedule(schedule) {
      this.schedule = schedule
        ? { ...schedule, entries: this.normalizeScheduleRoomSlots(schedule.entries || []), monitor_notes: [...(schedule.monitor_notes || [])] }
        : null;
      this.cellSelections = {};
      this.monitorNoteValues = {};
      this.eventNoteValues = {};
      if (this.schedule) {
        this.schedule.monitor_notes.forEach((note) => {
          const key = this.monitorNoteKey(note.shift_template_id, note.schedule_date);
          this.monitorNoteValues[key] = note.note || '';
          this.eventNoteValues[key] = note.event_note || '';
        });
        this.shiftTemplates.forEach((shift) => {
          this.weekDays.forEach((day) => {
            const key = this.monitorNoteKey(shift.id, day.iso);
            if (!(key in this.monitorNoteValues)) this.monitorNoteValues[key] = '';
            if (!(key in this.eventNoteValues)) this.eventNoteValues[key] = '';
          });
        });
      }
      this.saveState = 'saved';
      this.editRevision = 0;
      this.saveRequestedWhileInFlight = false;
      this.undoStack = [];
      this.fieldEditUndoSnapshot = null;
    },
    captureUndoState() {
      if (!this.schedule) return null;
      return {
        scheduleId: Number(this.schedule.id),
        studioId: Number(this.studioId),
        weekStart: this.weekStart,
        entries: JSON.parse(JSON.stringify(this.schedule.entries || [])),
        monitorNoteValues: { ...this.monitorNoteValues },
        eventNoteValues: { ...this.eventNoteValues },
      };
    },
    undoStateFingerprint(state) {
      if (!state) return '';
      return JSON.stringify([state.entries, state.monitorNoteValues, state.eventNoteValues]);
    },
    recordUndoState() {
      const snapshot = this.captureUndoState();
      if (!snapshot) return;
      const previous = this.undoStack[this.undoStack.length - 1];
      if (previous && this.undoStateFingerprint(previous) === this.undoStateFingerprint(snapshot)) return;
      this.undoStack.push(snapshot);
      if (this.undoStack.length > 20) this.undoStack.shift();
    },
    pushFieldUndoSnapshot(snapshot) {
      if (!snapshot) return;
      const current = this.captureUndoState();
      if (!current || this.undoStateFingerprint(snapshot) === this.undoStateFingerprint(current)) return;
      const previous = this.undoStack[this.undoStack.length - 1];
      if (!previous || this.undoStateFingerprint(previous) !== this.undoStateFingerprint(snapshot)) {
        this.undoStack.push(snapshot);
        if (this.undoStack.length > 20) this.undoStack.shift();
      }
    },
    startFieldEditUndo() {
      if (!this.fieldEditUndoSnapshot) this.fieldEditUndoSnapshot = this.captureUndoState();
    },
    finishFieldEditUndo() {
      this.pushFieldUndoSnapshot(this.fieldEditUndoSnapshot);
      this.fieldEditUndoSnapshot = null;
    },
    handleUndoShortcut(event) {
      const target = event.target;
      const isTextControl = target instanceof Element
        && !!target.closest('input, textarea, select, [contenteditable="true"]');
      if (isTextControl || !this.isEditing || event.shiftKey
        || !(event.ctrlKey || event.metaKey) || String(event.key).toLowerCase() !== 'z') return;
      event.preventDefault();
      this.undoLastChange();
    },
    undoLastChange() {
      if (!this.schedule || !this.undoStack.length) return;
      const snapshot = this.undoStack.pop();
      if (Number(snapshot.scheduleId) !== Number(this.schedule.id)
        || Number(snapshot.studioId) !== Number(this.studioId)
        || snapshot.weekStart !== this.weekStart) {
        this.undoStack = [];
        return;
      }
      clearTimeout(this.saveTimer);
      this.schedule.entries = JSON.parse(JSON.stringify(snapshot.entries));
      this.monitorNoteValues = { ...snapshot.monitorNoteValues };
      this.eventNoteValues = { ...snapshot.eventNoteValues };
      this.selectedModelIds = [];
      this.selectedGuestName = '';
      this.fieldEditUndoSnapshot = null;
      this.queueSave();
      toast.info('Last schedule change undone');
    },
    moveWeek(offset) {
      const nextWeek = format(addWeeks(parseISO(this.weekStart), offset), 'yyyy-MM-dd');
      if (this.profileView && this.maxProfileWeekStart && nextWeek > this.maxProfileWeekStart) return;
      this.weekStart = nextWeek;
      this.syncComparisonRoute();
      this.loadStudioSchedule();
    },
    goToCurrentWeek() {
      this.weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
      this.syncComparisonRoute();
      this.loadStudioSchedule();
    },
    syncComparisonRoute() {
      if (!this.isComparisonWindow || !this.studioId) return;
      const studioId = String(this.studioId);
      if (String(this.$route.query.studio_id || '') === studioId
        && String(this.$route.query.week_start || '') === this.weekStart) return;
      this.$router.replace({
        name: 'ModelSchedule',
        query: { compare: '1', studio_id: studioId, week_start: this.weekStart },
      }).catch(() => {});
    },
    handleCompareClick() {
      if (this.isComparisonWindow) {
        window.close();
        return;
      }
      const comparisonWeek = format(addWeeks(parseISO(this.weekStart), -1), 'yyyy-MM-dd');
      const route = this.$router.resolve({
        name: 'ModelSchedule',
        query: { compare: '1', studio_id: this.studioId, week_start: comparisonWeek },
      });
      const width = Math.min(1500, window.screen.availWidth || 1500);
      const height = Math.min(950, window.screen.availHeight || 950);
      const left = Math.max(0, Math.round(((window.screen.availWidth || width) - width) / 2));
      const top = Math.max(0, Math.round(((window.screen.availHeight || height) - height) / 2));
      const comparisonWindow = window.open(
        new URL(route.href, window.location.origin).href,
        'modato-schedule-comparison',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
      if (!comparisonWindow) {
        toast.error('Allow popups for Modato to open Schedule Comparison');
        return;
      }
      comparisonWindow.focus();
    },
    openNewWeekDialog() {
      if (!this.canEditSelectedStudio) return;
      this.creationMode = this.basePatterns.length ? 'base' : 'copy';
      this.newWeekDialog = true;
    },
    async createSchedule() {
      if (!this.canEditSelectedStudio) return;
      this.creating = true;
      try {
        const response = await secureAxios.post('/api/model-schedule/week', {
          studio_id: this.studioId,
          week_start: this.weekStart,
          mode: this.creationMode,
          source_week_start: format(addWeeks(parseISO(this.weekStart), -1), 'yyyy-MM-dd'),
        });
        this.applySchedule(response.data);
        this.isEditing = true;
        this.newWeekDialog = false;
        toast.success('Weekly schedule created');
      } catch (error) {
        this.error = error?.response?.data?.message || 'Unable to create the schedule.';
      } finally {
        this.creating = false;
      }
    },
    monitorNoteKey(shiftId, date) {
      return `${shiftId}:${date}`;
    },
    cellSelectionKey(shiftId, dayIndex, roomSlot) {
      return `${shiftId}:${dayIndex}:${roomSlot}`;
    },
    normalizeScheduleRoomSlots(entries) {
      const usedSlots = new Map();
      return entries.map((entry) => {
        if (!entry.shift_template_id) return { ...entry, room_slot: null };

        const key = `${entry.shift_template_id}:${entry.day_of_week}`;
        if (!usedSlots.has(key)) usedSlots.set(key, new Set());
        const assignedSlots = usedSlots.get(key);
        let roomSlot = Number(entry.room_slot);
        if (!Number.isInteger(roomSlot) || roomSlot < 1 || roomSlot > 7 || assignedSlots.has(roomSlot)) {
          roomSlot = this.roomSlots.find((room) => !assignedSlots.has(room.slot))?.slot || null;
        }
        if (roomSlot) assignedSlots.add(roomSlot);
        return { ...entry, room_slot: roomSlot };
      });
    },
    displayModel(modelId) {
      const model = this.models.find((item) => Number(item.id) === Number(modelId));
      if (!model) return 'Model';
      const fullNameParts = String(model.fullName || '').trim().split(/\s+/).filter(Boolean);
      const firstName = fullNameParts[0] || model.stageName || 'Model';
      const surnameIndex = fullNameParts.length >= 3 ? 2 : 1;
      const lastName = fullNameParts[surnameIndex] || fullNameParts[fullNameParts.length - 1] || '';

      if (this.modelNameView === 'custom') {
        return model.scheduleDisplayName || firstName;
      }
      if (this.modelNameView === 'first_name') return firstName;
      if (this.modelNameView === 'first_initial') return lastName ? `${firstName} ${lastName.charAt(0)}.` : firstName;
      if (this.modelNameView === 'first_last') return lastName ? `${firstName} ${lastName}` : firstName;
      return model.stageName || model.fullName || 'Model';
    },
    modelDisplayName(model) {
      return model ? this.displayModel(model.id) : 'Model';
    },
    openCustomNameDialog(modelId) {
      const model = this.models.find((item) => Number(item.id) === Number(modelId));
      if (!model) return;
      this.customNameModel = model;
      this.customNameDraft = String(model.scheduleDisplayName || '');
      this.customNameDialog = true;
    },
    closeCustomNameDialog() {
      if (this.savingCustomName) return;
      this.customNameDialog = false;
      this.customNameModel = null;
      this.customNameDraft = '';
    },
    async persistCustomName(customName) {
      if (!this.canEditSelectedStudio || !this.customNameModel || this.savingCustomName) return;
      this.savingCustomName = true;
      try {
        const response = await secureAxios.patch(`/api/model-schedule/models/${this.customNameModel.id}/custom-name`, {
          studio_id: this.studioId,
          custom_name: customName,
        });
        this.customNameModel.scheduleDisplayName = response.data.custom_name || null;
        this.customNameDialog = false;
        this.customNameModel = null;
        this.customNameDraft = '';
        toast.success('Custom schedule name saved');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Unable to save the custom schedule name');
      } finally {
        this.savingCustomName = false;
      }
    },
    saveCustomName() {
      this.persistCustomName(this.customNameDraft.trim());
    },
    clearCustomName() {
      this.customNameDraft = '';
      this.persistCustomName('');
    },
    entryLabel(entry) {
      if (!entry) return '';
      if (entry.assignment_type === 'scheduled') return this.displayModel(entry.model_id);
      if (entry.assignment_type === 'guest') return entry.custom_name || 'Guest Model';
      return this.reasonLabel(entry.assignment_type);
    },
    isModelAssignment(entry) {
      return ['scheduled', 'guest'].includes(entry?.assignment_type);
    },
    entryTextColor(entry) {
      const selected = String(entry?.text_color || '').toUpperCase();
      return this.textColorOptions.some((color) => color.value === selected) ? selected : '#202020';
    },
    setEntryTextColor(entry, color) {
      if (!entry || !this.textColorOptions.some((option) => option.value === color)) return;
      if (this.entryTextColor(entry) === color) return;
      this.recordUndoState();
      entry.text_color = color;
      this.queueSave();
    },
    toggleModelFocus(entry) {
      if (!this.schedule || !this.isModelAssignment(entry)) return;
      const wasSelected = Number(entry.is_model_focus) === 1 || entry.is_model_focus === true;
      this.recordUndoState();
      this.schedule.entries.forEach((candidate) => {
        if (Number(candidate.shift_template_id) === Number(entry.shift_template_id)
          && Number(candidate.day_of_week) === Number(entry.day_of_week)) {
          candidate.is_model_focus = 0;
        }
      });
      if (!wasSelected) entry.is_model_focus = 1;
      this.queueSave();
    },
    handleScheduleCellClick(shiftId, dayIndex, roomSlot) {
      const entry = this.roomEntry(shiftId, dayIndex, roomSlot);
      if (!this.isEditing && entry?.note) {
        this.openEntryNoteDialog(entry);
        return;
      }
      this.applySelectedModelsToShift(shiftId, dayIndex, roomSlot);
    },
    openEntryNoteDialog(entry) {
      if (!entry) return;
      this.entryNoteEntry = entry;
      this.entryNoteDraft = String(entry.note || '');
      this.entryNoteDialog = true;
    },
    closeEntryNoteDialog() {
      this.entryNoteDialog = false;
      this.entryNoteEntry = null;
      this.entryNoteDraft = '';
    },
    saveEntryNote() {
      if (!this.entryNoteEntry) return;
      const note = String(this.entryNoteDraft || '').trim().slice(0, 2000);
      if (!note) return;
      if (String(this.entryNoteEntry.note || '') === note) {
        this.closeEntryNoteDialog();
        return;
      }
      this.recordUndoState();
      this.entryNoteEntry.note = note;
      this.queueSave();
      this.closeEntryNoteDialog();
    },
    clearEntryNote() {
      if (!this.entryNoteEntry?.note) return;
      this.recordUndoState();
      this.entryNoteEntry.note = null;
      this.queueSave();
      this.closeEntryNoteDialog();
    },
    openWorkUntilDialog(entry) {
      if (!this.isModelAssignment(entry)) return;
      this.workUntilEntry = entry;
      this.workFromDraft = String(entry.work_from || '').slice(0, 5);
      this.workUntilDraft = String(entry.work_until || '').slice(0, 5);
      this.workUntilDialog = true;
    },
    closeWorkUntilDialog() {
      this.workUntilDialog = false;
      this.workUntilEntry = null;
      this.workFromDraft = '';
      this.workUntilDraft = '';
    },
    saveWorkUntil() {
      const validTime = (value) => !value || /^\d{2}:\d{2}$/.test(value);
      if (!this.workUntilEntry || (!this.workFromDraft && !this.workUntilDraft)
        || !validTime(this.workFromDraft) || !validTime(this.workUntilDraft)) return;
      const workFrom = this.workFromDraft || null;
      const workUntil = this.workUntilDraft || null;
      if ((this.workUntilEntry.work_from || null) === workFrom && (this.workUntilEntry.work_until || null) === workUntil) {
        this.closeWorkUntilDialog();
        return;
      }
      this.recordUndoState();
      this.workUntilEntry.work_from = workFrom;
      this.workUntilEntry.work_until = workUntil;
      this.queueSave();
      this.closeWorkUntilDialog();
    },
    clearWorkUntil() {
      if (!this.workUntilEntry || (!this.workUntilEntry.work_from && !this.workUntilEntry.work_until)) return;
      this.recordUndoState();
      this.workUntilEntry.work_from = null;
      this.workUntilEntry.work_until = null;
      this.queueSave();
      this.closeWorkUntilDialog();
    },
    roomEntry(shiftId, dayIndex, roomSlot) {
      return (this.schedule?.entries || []).find((entry) => (
        Number(entry.shift_template_id) === Number(shiftId)
        && Number(entry.day_of_week) === Number(dayIndex)
        && Number(entry.room_slot) === Number(roomSlot)
      )) || null;
    },
    reasonLabel(value) {
      return this.reasonOptions.find((reason) => reason.value === value)?.label || 'Reason';
    },
    statusChipClass(value) {
      return ['scheduled', 'guest'].includes(value) ? '' : `schedule-model-chip--${String(value).replace(/_/g, '-')}`;
    },
    displayedRoomSlots(shiftId) {
      if (!this.isScreenshotting) return this.roomSlots;
      return this.roomSlots.filter((room) => this.roomEntry(shiftId, this.weekDays[0].index, room.slot)
        || this.weekDays.some((day) => this.roomEntry(shiftId, day.index, room.slot)));
    },
    availableModelsForDay(dayIndex, shiftId) {
      const assignedIds = new Set((this.schedule?.entries || [])
        // A model can cover more than one shift on the same date. Only prevent
        // duplicate placement in the same shift/day.
        .filter((entry) => (
          entry.assignment_type === 'scheduled'
          && Number(entry.day_of_week) === Number(dayIndex)
          && Number(entry.shift_template_id) === Number(shiftId)
        ))
        .map((entry) => Number(entry.model_id)));
      return this.models.filter((model) => !assignedIds.has(Number(model.id)));
    },
    modelsForCell(dayIndex, shiftId, currentModelId) {
      const available = this.availableModelsForDay(dayIndex, shiftId);
      const current = this.models.find((model) => Number(model.id) === Number(currentModelId));
      return current && !available.some((model) => Number(model.id) === Number(current.id))
        ? [current, ...available]
        : available;
    },
    async toggleEditMode() {
      if (!this.canEditSelectedStudio) return;
      if (!this.isEditing) {
        this.isEditing = true;
        return;
      }
      this.isEditing = false;
      this.clearModelSelection();
      if (this.saveState === 'unsaved') {
        clearTimeout(this.saveTimer);
        await this.saveSchedule();
      }
    },
    isModelSelected(modelId) {
      return this.selectedModelIds.includes(Number(modelId));
    },
    toggleModelSelection(modelId) {
      const normalizedId = Number(modelId);
      this.selectedGuestName = '';
      this.selectedModelIds = this.isModelSelected(normalizedId)
        ? this.selectedModelIds.filter((id) => id !== normalizedId)
        : [...this.selectedModelIds, normalizedId];
    },
    clearModelSelection() {
      this.selectedModelIds = [];
      this.selectedGuestName = '';
    },
    openGuestModelDialog() {
      this.guestNameDraft = '';
      this.guestModelDialog = true;
    },
    closeGuestModelDialog() {
      this.guestModelDialog = false;
      this.guestNameDraft = '';
    },
    confirmGuestModel() {
      const name = String(this.guestNameDraft || '').trim().slice(0, 80);
      if (!name) return;
      this.selectedModelIds = [];
      this.selectedGuestName = name;
      this.closeGuestModelDialog();
    },
    toggleGuestSelection(name) {
      const normalizedName = String(name || '').trim();
      if (!normalizedName) return;
      this.selectedModelIds = [];
      this.selectedGuestName = this.selectedGuestName === normalizedName ? '' : normalizedName;
    },
    dragCellKey(shiftId, dayIndex, roomSlot) {
      return `${Number(shiftId)}:${Number(dayIndex)}:${Number(roomSlot)}`;
    },
    startAssignmentDrag(event, shiftId, dayIndex, roomSlot) {
      if (!this.isEditing) return;
      const entry = this.roomEntry(shiftId, dayIndex, roomSlot);
      if (!this.isModelAssignment(entry)) {
        event.preventDefault();
        return;
      }
      this.draggedAssignment = {
        shiftId: Number(shiftId),
        dayIndex: Number(dayIndex),
        roomSlot: Number(roomSlot),
        assignmentType: entry.assignment_type,
        modelId: entry.assignment_type === 'scheduled' ? Number(entry.model_id) : null,
        customName: entry.assignment_type === 'guest' ? String(entry.custom_name || '') : null,
      };
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', this.dragCellKey(shiftId, dayIndex, roomSlot));
    },
    endAssignmentDrag() {
      this.draggedAssignment = null;
      this.dragTargetKey = null;
    },
    setDragTarget(shiftId, dayIndex, roomSlot) {
      if (!this.draggedAssignment) return;
      this.dragTargetKey = this.dragCellKey(shiftId, dayIndex, roomSlot);
    },
    clearDragTarget(shiftId, dayIndex, roomSlot) {
      if (this.dragTargetKey === this.dragCellKey(shiftId, dayIndex, roomSlot)) {
        this.dragTargetKey = null;
      }
    },
    isDragTarget(shiftId, dayIndex, roomSlot) {
      return !!this.draggedAssignment
        && this.dragTargetKey === this.dragCellKey(shiftId, dayIndex, roomSlot);
    },
    moveDraggedAssignment(targetShiftId, targetDayIndex, targetRoomSlot) {
      const source = this.draggedAssignment;
      this.dragTargetKey = null;
      if (!source || !this.schedule) return;

      const sameCell = source.shiftId === Number(targetShiftId)
        && source.dayIndex === Number(targetDayIndex)
        && source.roomSlot === Number(targetRoomSlot);
      if (sameCell) {
        this.endAssignmentDrag();
        return;
      }

      if (this.roomEntry(targetShiftId, targetDayIndex, targetRoomSlot)) {
        toast.info('That cell is already occupied. Choose an empty cell.');
        this.endAssignmentDrag();
        return;
      }

      const duplicate = this.schedule.entries.some((entry) => {
        const sameIdentity = source.assignmentType === 'guest'
          ? entry.assignment_type === 'guest' && String(entry.custom_name || '').toLowerCase() === source.customName.toLowerCase()
          : entry.assignment_type === 'scheduled' && Number(entry.model_id) === source.modelId;
        return sameIdentity
          && Number(entry.shift_template_id) === Number(targetShiftId)
          && Number(entry.day_of_week) === Number(targetDayIndex)
          && !(Number(entry.shift_template_id) === source.shiftId
            && Number(entry.day_of_week) === source.dayIndex
            && Number(entry.room_slot) === source.roomSlot);
      });
      if (duplicate) {
        toast.info(`${source.assignmentType === 'guest' ? source.customName : this.displayModel(source.modelId)} is already assigned to that shift and day.`);
        this.endAssignmentDrag();
        return;
      }

      const sourceEntry = this.roomEntry(source.shiftId, source.dayIndex, source.roomSlot);
      const sourceStillMatches = source.assignmentType === 'guest'
        ? sourceEntry?.assignment_type === 'guest' && String(sourceEntry.custom_name || '') === source.customName
        : sourceEntry?.assignment_type === 'scheduled' && Number(sourceEntry.model_id) === source.modelId;
      if (!sourceStillMatches) {
        this.endAssignmentDrag();
        return;
      }
      this.recordUndoState();
      sourceEntry.shift_template_id = Number(targetShiftId);
      sourceEntry.day_of_week = Number(targetDayIndex);
      sourceEntry.room_slot = Number(targetRoomSlot);
      this.endAssignmentDrag();
      this.queueSave();
    },
    requestClearRow(shiftId, roomSlot) {
      if (!this.schedule) return;
      const hasEntries = this.schedule.entries.some((entry) => (
        Number(entry.shift_template_id) === Number(shiftId)
        && Number(entry.room_slot) === Number(roomSlot)
      ));
      if (!hasEntries) return;
      this.recordUndoState();
      this.schedule.entries = this.schedule.entries.filter((entry) => !(
        Number(entry.shift_template_id) === Number(shiftId)
        && Number(entry.room_slot) === Number(roomSlot)
      ));
      this.queueSave();
    },
    clearShiftDayColumn(shiftId, dayIndex) {
      if (!this.schedule) return;
      const day = this.weekDays.find((item) => Number(item.index) === Number(dayIndex));
      const noteKey = day ? this.monitorNoteKey(shiftId, day.iso) : null;
      const hasEntries = this.schedule.entries.some((entry) => (
        Number(entry.shift_template_id) === Number(shiftId)
        && Number(entry.day_of_week) === Number(dayIndex)
      ));
      if (!hasEntries && (!noteKey || !this.eventNoteValues[noteKey])) return;
      this.recordUndoState();
      this.schedule.entries = this.schedule.entries.filter((entry) => !(
        Number(entry.shift_template_id) === Number(shiftId)
        && Number(entry.day_of_week) === Number(dayIndex)
      ));
      if (noteKey) this.eventNoteValues[noteKey] = '';
      this.queueSave();
    },
    requestClearWeek() {
      this.clearRequest = { type: 'week' };
      this.clearDialog = true;
    },
    requestClearShift(shiftId) {
      this.clearRequest = { type: 'shift', shiftId: Number(shiftId) };
      this.clearDialog = true;
    },
    cancelClear() {
      this.clearDialog = false;
      this.clearRequest = null;
    },
    async confirmClear() {
      if (!this.schedule || !this.clearRequest) return;
      if (this.clearRequest.type === 'shift') {
        const shiftId = Number(this.clearRequest.shiftId);
        const hasEntries = this.schedule.entries.some((entry) => Number(entry.shift_template_id) === shiftId);
        if (!hasEntries) {
          this.cancelClear();
          return;
        }
        this.recordUndoState();
        this.schedule.entries = this.schedule.entries.filter((entry) => Number(entry.shift_template_id) !== shiftId);
        this.cancelClear();
        this.queueSave();
        return;
      }
      const scheduleId = this.schedule.id;
      clearTimeout(this.saveTimer);
      this.cancelClear();
      try {
        await secureAxios.delete(`/api/model-schedule/week/${scheduleId}`, {
          params: { studio_id: this.studioId },
        });
        this.applySchedule(null);
        this.isEditing = false;
        toast.success('Week cleared. Choose how you want to create it again.');
      } catch (error) {
        this.error = error?.response?.data?.message || 'Unable to clear this schedule week.';
      }
    },
    applySelectedModelsToShift(shiftId, dayIndex, roomSlot) {
      if (!this.isEditing || !this.schedule || !this.hasPlacementSelection) return;
      const existingEntry = this.roomEntry(shiftId, dayIndex, roomSlot);
      if (this.isModelAssignment(existingEntry)) return;

      if (this.selectedGuestName) {
        const guestName = this.selectedGuestName;
        const duplicate = this.schedule.entries.some((entry) => (
          entry.assignment_type === 'guest'
          && String(entry.custom_name || '').toLowerCase() === guestName.toLowerCase()
          && Number(entry.day_of_week) === Number(dayIndex)
          && Number(entry.shift_template_id) === Number(shiftId)
        ));
        if (duplicate) {
          toast.info(`${guestName} is already assigned to this shift and day.`);
          return;
        }
        this.recordUndoState();
        if (existingEntry) this.removeRoomEntry(shiftId, dayIndex, roomSlot, false);
        this.schedule.entries.push({
          model_id: null,
          custom_name: guestName,
          day_of_week: Number(dayIndex),
          shift_template_id: Number(shiftId),
          room_slot: Number(roomSlot),
          assignment_type: 'guest',
        });
        this.queueSave();
        return;
      }

      const modelId = this.selectedModelIds[0];
      if (!this.availableModelsForDay(dayIndex, shiftId).some((model) => Number(model.id) === Number(modelId))) return;
      this.recordUndoState();
      if (existingEntry) this.removeRoomEntry(shiftId, dayIndex, roomSlot, false);
      this.schedule.entries.push({
        model_id: Number(modelId),
        day_of_week: Number(dayIndex),
        shift_template_id: Number(shiftId),
        room_slot: Number(roomSlot),
        assignment_type: 'scheduled',
      });
      if (this.selectedModelIds.length > 1) this.selectedModelIds = this.selectedModelIds.slice(1);
      this.queueSave();
    },
    applySelectedModelsToEntireShift(shiftId) {
      if (!this.schedule || !this.selectedModelIds.length) return;
      const undoSnapshot = this.captureUndoState();
      let addedCount = 0;

      this.weekDays.forEach((day) => {
        const availableIds = new Set(this.availableModelsForDay(day.index, shiftId).map((model) => Number(model.id)));
        this.selectedModelIds.forEach((modelId) => {
          if (!availableIds.has(Number(modelId))) return;
          const roomSlot = this.roomSlots.find((room) => !this.roomEntry(shiftId, day.index, room.slot))?.slot;
          if (!roomSlot) return;
          this.schedule.entries.push({
            model_id: Number(modelId),
            day_of_week: Number(day.index),
            shift_template_id: Number(shiftId),
            room_slot: roomSlot,
            assignment_type: 'scheduled',
          });
          addedCount += 1;
        });
      });

      if (addedCount) {
        this.pushFieldUndoSnapshot(undoSnapshot);
        this.queueSave();
      }
    },
    copyAssignmentToNextDay(sourceEntry, shiftId, roomSlot, dayIndex) {
      if (!this.isModelAssignment(sourceEntry)) return;
      const nextDayIndex = Number(dayIndex) + 1;
      if (nextDayIndex > 6) {
        toast.info('Sunday is the last day of this schedule.');
        return;
      }
      const targetEntry = this.roomEntry(shiftId, nextDayIndex, roomSlot);
      if (this.isModelAssignment(targetEntry)) {
        toast.info(`Room ${roomSlot === 7 ? 'J' : roomSlot} is already assigned on ${this.weekDays[nextDayIndex].short}.`);
        return;
      }
      const duplicate = sourceEntry.assignment_type === 'guest'
        ? this.schedule.entries.some((entry) => entry.assignment_type === 'guest'
          && String(entry.custom_name || '').toLowerCase() === String(sourceEntry.custom_name || '').toLowerCase()
          && Number(entry.day_of_week) === nextDayIndex
          && Number(entry.shift_template_id) === Number(shiftId))
        : !this.availableModelsForDay(nextDayIndex, shiftId).some((model) => Number(model.id) === Number(sourceEntry.model_id));
      if (duplicate) {
        toast.info(`${this.entryLabel(sourceEntry)} is already assigned to this shift on ${this.weekDays[nextDayIndex].short}.`);
        return;
      }
      this.recordUndoState();
      if (targetEntry) this.removeRoomEntry(shiftId, nextDayIndex, roomSlot, false);
      this.schedule.entries.push({
        model_id: sourceEntry.assignment_type === 'scheduled' ? Number(sourceEntry.model_id) : null,
        custom_name: sourceEntry.assignment_type === 'guest' ? sourceEntry.custom_name : null,
        day_of_week: nextDayIndex,
        shift_template_id: Number(shiftId),
        room_slot: Number(roomSlot),
        assignment_type: sourceEntry.assignment_type,
        text_color: sourceEntry.text_color || '#202020',
        work_from: sourceEntry.work_from || null,
        work_until: sourceEntry.work_until || null,
        note: sourceEntry.note || null,
      });
      this.queueSave();
    },
    copyAssignmentAcrossWeek(sourceEntry, shiftId, roomSlot) {
      if (!this.isModelAssignment(sourceEntry)) return;
      const undoSnapshot = this.captureUndoState();
      let addedCount = 0;
      let occupiedCount = 0;
      this.weekDays.forEach((day) => {
        const targetEntry = this.roomEntry(shiftId, day.index, roomSlot);
        if (this.isModelAssignment(targetEntry)) {
          const sameAssignment = sourceEntry.assignment_type === 'guest'
            ? targetEntry.assignment_type === 'guest' && String(targetEntry.custom_name || '').toLowerCase() === String(sourceEntry.custom_name || '').toLowerCase()
            : targetEntry.assignment_type === 'scheduled' && Number(targetEntry.model_id) === Number(sourceEntry.model_id);
          if (!sameAssignment) occupiedCount += 1;
          return;
        }
        const duplicate = sourceEntry.assignment_type === 'guest'
          ? this.schedule.entries.some((entry) => entry.assignment_type === 'guest'
            && String(entry.custom_name || '').toLowerCase() === String(sourceEntry.custom_name || '').toLowerCase()
            && Number(entry.day_of_week) === Number(day.index)
            && Number(entry.shift_template_id) === Number(shiftId))
          : !this.availableModelsForDay(day.index, shiftId).some((model) => Number(model.id) === Number(sourceEntry.model_id));
        if (duplicate) return;
        if (targetEntry) this.removeRoomEntry(shiftId, day.index, roomSlot, false);
        this.schedule.entries.push({
          model_id: sourceEntry.assignment_type === 'scheduled' ? Number(sourceEntry.model_id) : null,
          custom_name: sourceEntry.assignment_type === 'guest' ? sourceEntry.custom_name : null,
          day_of_week: day.index,
          shift_template_id: Number(shiftId),
          room_slot: Number(roomSlot),
          assignment_type: sourceEntry.assignment_type,
          text_color: sourceEntry.text_color || '#202020',
          work_from: sourceEntry.work_from || null,
          work_until: sourceEntry.work_until || null,
          note: sourceEntry.note || null,
        });
        addedCount += 1;
      });
      if (addedCount) {
        this.pushFieldUndoSnapshot(undoSnapshot);
        this.queueSave();
      }
      if (occupiedCount) toast.info(`${occupiedCount} day${occupiedCount === 1 ? ' was' : 's were'} kept because another model is already scheduled.`);
    },
    addModelToShift(modelId, shiftId, dayIndex, roomSlot) {
      if (!modelId || !this.schedule || !this.availableModelsForDay(dayIndex, shiftId).some((model) => Number(model.id) === Number(modelId))) return;
      const existingEntry = this.roomEntry(shiftId, dayIndex, roomSlot);
      if (existingEntry?.assignment_type === 'scheduled') return;
      this.recordUndoState();
      if (existingEntry) this.removeRoomEntry(shiftId, dayIndex, roomSlot, false);
      this.schedule.entries.push({
        model_id: Number(modelId),
        day_of_week: Number(dayIndex),
        shift_template_id: Number(shiftId),
        room_slot: Number(roomSlot),
        assignment_type: 'scheduled',
      });
      this.cellSelections[this.cellSelectionKey(shiftId, dayIndex, roomSlot)] = null;
      this.queueSave();
    },
    setCellModel(modelId, shiftId, dayIndex, roomSlot) {
      if (!modelId || !this.schedule) return;
      const existingEntry = this.roomEntry(shiftId, dayIndex, roomSlot);
      if (existingEntry?.assignment_type === 'scheduled' && Number(existingEntry.model_id) === Number(modelId)) return;
      if (!this.modelsForCell(dayIndex, shiftId, existingEntry?.model_id)
        .some((model) => Number(model.id) === Number(modelId))) return;
      this.recordUndoState();
      if (existingEntry) this.removeRoomEntry(shiftId, dayIndex, roomSlot, false);
      this.schedule.entries.push({
        model_id: Number(modelId),
        day_of_week: Number(dayIndex),
        shift_template_id: Number(shiftId),
        room_slot: Number(roomSlot),
        assignment_type: 'scheduled',
      });
      this.queueSave();
    },
    applyReasonToCell(reason, shiftId, dayIndex, roomSlot) {
      if (!reason || !this.schedule) return;
      const existingEntry = this.roomEntry(shiftId, dayIndex, roomSlot);
      if (existingEntry?.assignment_type === reason && !existingEntry.model_id) return;
      this.recordUndoState();
      if (existingEntry) {
        existingEntry.model_id = null;
        existingEntry.custom_name = null;
        existingEntry.assignment_type = reason;
        existingEntry.text_color = '#202020';
        existingEntry.work_from = null;
        existingEntry.work_until = null;
        existingEntry.is_model_focus = 0;
      } else {
        this.schedule.entries.push({
          model_id: null,
          day_of_week: Number(dayIndex),
          shift_template_id: Number(shiftId),
          room_slot: Number(roomSlot),
          assignment_type: reason,
        });
      }
      this.queueSave();
    },
    removeRoomEntry(shiftId, dayIndex, roomSlot, shouldSave = true) {
      if (!this.schedule) return;
      const hasEntry = this.schedule.entries.some((entry) => (
        Number(entry.shift_template_id) === Number(shiftId)
        && Number(entry.day_of_week) === Number(dayIndex)
        && Number(entry.room_slot) === Number(roomSlot)
      ));
      if (!hasEntry) return;
      if (shouldSave) this.recordUndoState();
      this.schedule.entries = this.schedule.entries.filter((entry) => !(
        Number(entry.shift_template_id) === Number(shiftId)
        && Number(entry.day_of_week) === Number(dayIndex)
        && Number(entry.room_slot) === Number(roomSlot)
      ));
      if (shouldSave) this.queueSave();
    },
    buildMonitorNotes() {
      return this.shiftTemplates.flatMap((shift) => this.weekDays.map((day) => ({
        shift_template_id: shift.id,
        schedule_date: day.iso,
        note: this.monitorNoteValues[this.monitorNoteKey(shift.id, day.iso)] || '',
        event_note: this.eventNoteValues[this.monitorNoteKey(shift.id, day.iso)] || '',
      })));
    },
    queueSave() {
      if (!this.canEditSelectedStudio || !this.schedule) return;
      clearTimeout(this.saveTimer);
      this.editRevision += 1;
      this.saveState = 'unsaved';
      this.saveTimer = setTimeout(this.saveSchedule, 700);
    },
    async saveSchedule() {
      if (!this.canEditSelectedStudio || !this.schedule) return;
      if (this.saveInFlight) {
        this.saveRequestedWhileInFlight = true;
        return;
      }
      const scheduleId = this.schedule.id;
      const studioId = this.studioId;
      const weekStart = this.weekStart;
      const revisionBeingSaved = this.editRevision;
      const entriesBeingSaved = JSON.parse(JSON.stringify(this.schedule.entries));
      const monitorNotesBeingSaved = this.buildMonitorNotes();
      let savedSuccessfully = false;
      this.saveInFlight = true;
      this.saveRequestedWhileInFlight = false;
      this.saveState = 'saving';
      try {
        await secureAxios.put(`/api/model-schedule/week/${scheduleId}`, {
          studio_id: studioId,
          entries: entriesBeingSaved,
          monitor_notes: monitorNotesBeingSaved,
        });
        savedSuccessfully = true;
        if (Number(this.schedule?.id) === Number(scheduleId)
          && Number(this.studioId) === Number(studioId)
          && this.weekStart === weekStart) {
          // Do not rebuild the active form from the server response. Replacing
          // monitorNoteValues while a person is typing moves the text cursor
          // and can overwrite characters entered during the request.
          if (this.editRevision === revisionBeingSaved) {
            this.schedule.monitor_notes = monitorNotesBeingSaved;
            this.saveState = 'saved';
          } else {
            this.saveState = 'unsaved';
            this.saveRequestedWhileInFlight = true;
          }
        }
      } catch (error) {
        if (Number(this.schedule?.id) !== Number(scheduleId)
          || Number(this.studioId) !== Number(studioId)
          || this.weekStart !== weekStart) return;
        console.error('Unable to save schedule:', error?.message || error);
        this.saveState = 'error';
        this.error = error?.response?.data?.message || 'Schedule changes could not be saved.';
      } finally {
        this.saveInFlight = false;
        if (savedSuccessfully
          && this.saveRequestedWhileInFlight
          && Number(this.schedule?.id) === Number(scheduleId)
          && Number(this.studioId) === Number(studioId)
          && this.weekStart === weekStart) {
          this.saveRequestedWhileInFlight = false;
          clearTimeout(this.saveTimer);
          this.saveTimer = setTimeout(this.saveSchedule, 100);
        }
      }
    },
    async saveAsBasePattern() {
      if (!this.canEditSelectedStudio || !this.schedule) return;
      try {
        clearTimeout(this.saveTimer);
        while (this.saveInFlight) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        clearTimeout(this.saveTimer);
        this.saveRequestedWhileInFlight = false;
        const monitorNotes = this.buildMonitorNotes();
        await secureAxios.put(`/api/model-schedule/week/${this.schedule.id}`, {
          studio_id: this.studioId,
          entries: this.schedule.entries,
          monitor_notes: monitorNotes,
        });
        this.schedule.monitor_notes = monitorNotes;
        this.saveState = 'saved';
        await secureAxios.put(`/api/model-schedule/week/${this.schedule.id}/base-pattern`, { studio_id: this.studioId });
        this.basePatterns = [...this.schedule.entries];
        toast.success('Base pattern and monitor headers saved for future weeks');
      } catch (error) {
        this.error = error?.response?.data?.message || 'Unable to save the base pattern.';
      }
    },
    openShiftDialog() {
      if (!this.canEditSelectedStudio) return;
      this.editableShiftTemplates = this.shiftTemplates.map((template) => ({ ...template }));
      this.shiftDialog = true;
    },
    async saveShiftTemplates() {
      if (!this.canEditSelectedStudio) return;
      this.savingShifts = true;
      try {
        const response = await secureAxios.put('/api/model-schedule/shift-templates', {
          studio_id: this.studioId,
          templates: this.editableShiftTemplates,
        });
        this.shiftTemplates = response.data || [];
        this.shiftDialog = false;
        toast.success('Shift settings saved');
      } catch (error) {
        this.error = error?.response?.data?.message || 'Unable to save shift settings.';
      } finally {
        this.savingShifts = false;
      }
    },
    async toggleJokerVisibility() {
      if (!this.canEditSelectedStudio || !this.studioId || this.savingJokerSetting) return;
      const previousValue = this.jokerEnabled;
      this.jokerEnabled = !previousValue;
      this.savingJokerSetting = true;
      try {
        const response = await secureAxios.put('/api/model-schedule/settings/joker', {
          studio_id: this.studioId,
          enabled: this.jokerEnabled,
        });
        this.jokerEnabled = response.data?.joker_enabled !== false;
      } catch (error) {
        this.jokerEnabled = previousValue;
        this.error = error?.response?.data?.message || 'Unable to save the Joker setting.';
      } finally {
        this.savingJokerSetting = false;
      }
    },
    formatTime(value) {
      const [hourString, minute = '00'] = String(value || '00:00').split(':');
      const hour = Number(hourString);
      const suffix = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minute} ${suffix}`;
    },
    formatCompactTime(value) {
      const [hourString, minute = '00'] = String(value || '00:00').split(':');
      const hour = Number(hourString);
      const suffix = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return minute === '00' ? `${displayHour} ${suffix}` : `${displayHour}:${minute} ${suffix}`;
    },
    entryWorkTime(entry) {
      const start = entry?.work_from ? this.formatCompactTime(entry.work_from) : '';
      const end = entry?.work_until ? this.formatCompactTime(entry.work_until) : '';
      if (start && end) return `${start} – ${end}`;
      return start || end;
    },
    entryWorkTimeTitle(entry) {
      const start = entry?.work_from ? this.formatCompactTime(entry.work_from) : '';
      const end = entry?.work_until ? this.formatCompactTime(entry.work_until) : '';
      if (start && end) return `Working ${start} – ${end}`;
      if (start) return `Starting at ${start}`;
      return end ? `Working until ${end}` : '';
    },
    shiftHeaderColor(shift) {
      const value = shift?.color;
      const match = /^#([0-9a-f]{6})$/i.exec(String(value || ''));
      if (!match) return '#315F8C';
      const isEvening = String(shift?.name || '').toLowerCase() === 'evening';
      const amount = isEvening ? 0.36 : 0.18;
      const number = Number.parseInt(match[1], 16);
      const darken = (channel) => Math.max(0, Math.round(channel * (1 - amount)));
      const red = darken((number >> 16) & 255);
      const green = darken((number >> 8) & 255);
      const blue = darken(number & 255);
      return `#${[red, green, blue].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
    },
    async copyScheduleScreenshot() {
      if (!this.schedule || this.isCopyingScreenshot) return;
      this.isCopyingScreenshot = true;
      this.isScreenshotting = true;
      try {
        await this.$nextTick();
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(this.$refs.scheduleScreenshot, {
          backgroundColor: '#ffffff',
          logging: false,
          scale: 2,
          useCORS: true,
        });
        const image = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!image) throw new Error('Unable to create the schedule image.');
        if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
          throw new Error('Image clipboard is not supported by this browser.');
        }
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': image })]);
        toast.success('Schedule screenshot copied. Paste it into WhatsApp.');
      } catch (error) {
        console.error('Unable to copy schedule screenshot:', error);
        toast.error('Could not copy the screenshot. Please try again in Chrome.');
      } finally {
        this.isScreenshotting = false;
        this.isCopyingScreenshot = false;
      }
    },
  },
};
</script>

<style scoped>
.model-schedule-page { max-width: 1640px; margin: 0 auto; padding: 22px; color: #252525; }
.model-schedule-page--comparison { max-width: none; padding: 12px; }
.schedule-page-heading { display: flex; justify-content: space-between; gap: 20px; align-items: flex-end; margin: 4px 2px 18px; }
.schedule-eyebrow { color: #2f7db9; font-size: 11px; font-weight: 800; letter-spacing: 1.4px; }
.schedule-page-heading h1 { margin: 3px 0 4px; font-size: 28px; line-height: 1; letter-spacing: -0.6px; }
.schedule-page-heading p { margin: 0; color: #747474; font-size: 13px; }
.schedule-page-actions { display: flex; align-items: center; gap: 9px; }
.save-status { font-size: 12px; font-weight: 700; color: #7b7b7b; }
.save-status.saving, .save-status.unsaved { color: #a36d19; }.save-status.error { color: #c62828; }.save-status.saved { color: #52835b; }
.schedule-controls { display: grid; grid-template-columns: 145px 40px 40px minmax(315px, 1fr) auto; align-items: center; gap: 10px; padding: 11px 13px; margin-bottom: 16px; border: 1px solid #d8d8d8; border-radius: 6px; }
.schedule-studio-control { display: grid; grid-template-columns: 45px 1fr; align-items: center; gap: 6px; color: #555; font-size: 11px; font-weight: 800; letter-spacing: .7px; text-transform: uppercase; }
.schedule-name-control { position: relative; z-index: 2; display: flex; align-items: center; justify-content: center; }
.schedule-compare-control { position: relative; z-index: 2; display: flex; align-items: center; justify-content: center; }
.schedule-name-control :deep(.v-btn), .schedule-compare-control :deep(.v-btn) { width: 40px; height: 40px; border-color: #b9c1c9; border-radius: 4px; }
.schedule-week-control { position: relative; z-index: 1; display: flex; justify-content: center; align-items: center; gap: 8px; min-width: 0; transform: translateX(-42px); }
.schedule-week-title { min-width: 300px; text-align: center; }.schedule-week-title span { display: block; font-size: 25px; font-weight: 850; line-height: 1.2; }
.schedule-controls-actions { display: flex; align-items: center; justify-content: flex-end; gap: 3px; }
.model-schedule-page--profile .schedule-controls { position: relative; display: flex; justify-content: center; }
.model-schedule-page--profile .schedule-week-control { transform: none; }
.model-schedule-page--profile .schedule-controls-actions { position: absolute; right: 13px; }
.model-tray { margin-bottom: 16px; border: 1px solid #d5dce4; border-radius: 6px; overflow: hidden; }.model-tray--day-off { border-color: #9bc9a7; }.model-tray--day-off .model-tray__heading { background: #f0f8f1; border-bottom-color: #cae3d0; }.model-tray__heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 12px; background: #f4f8fb; border-bottom: 1px solid #dce4eb; }.model-tray__heading strong { display: block; color: #24496b; font-size: 12px; font-weight: 850; letter-spacing: .35px; text-transform: uppercase; }.model-tray--day-off .model-tray__heading strong { color: #397145; }.model-tray__heading span { color: #66727f; font-size: 11px; }.model-tray__actions { display: flex; align-items: center; gap: 2px; flex: 0 0 auto; }.model-tray__body { display: flex; align-items: flex-start; gap: 11px; padding: 9px 11px; }.model-tray__search { flex: 0 0 185px; }.model-tray__search :deep(.v-field__input) { min-height: 31px; font-size: 12px; }.model-tray__models { display: flex; flex: 1; flex-wrap: wrap; align-content: flex-start; gap: 5px; max-height: 92px; overflow-y: auto; padding: 1px; }.model-tray-chip { display: inline-flex; align-items: center; gap: 5px; min-height: 27px; padding: 3px 8px; border: 1px solid #cbd8e4; border-radius: 3px; background: #fff; color: #344c61; font-size: 11px; font-weight: 750; line-height: 1.1; cursor: pointer; transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease; }.model-tray-chip:hover { border-color: #6d9fca; background: #f3f8fc; }.model-tray-chip--selected { border-color: #3275ad; background: #3f84bd; color: #fff; box-shadow: inset 0 0 0 1px rgba(255,255,255,.16); }.model-tray-chip--day-off { border-color: #4c9a5d; background: #5ca86c; }.model-tray-chip__edit { flex: 0 0 auto; opacity: .72; }.model-tray-chip__edit:hover, .model-tray-chip__edit:focus { opacity: 1; }
.model-tray__heading > div > span { display: block; }
.model-tray-chip--guest { border-color: #b7a0d8; background: #f5f0fb; color: #68458f; }
.model-tray-chip--guest:hover { border-color: #8e6fbb; background: #ede3f8; }
.model-tray-chip--guest.model-tray-chip--selected { border-color: #7450a4; background: #7d5aae; color: #fff; }
.schedule-loading, .schedule-empty { min-height: 285px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 13px; text-align: center; border: 1px solid #dedede; border-radius: 6px; }.schedule-empty h2 { margin: 0; font-size: 18px; }.schedule-empty p { max-width: 410px; margin: 0 0 8px; color: #757575; font-size: 13px; }.schedule-empty__icon { color: #4f91cd; }
.schedule-grid-shell { display: grid; gap: 14px; }
.schedule-shift-section { border: 1px solid #cecece; border-radius: 6px; overflow: hidden; background: #fff; }.schedule-shift-heading { display: grid; grid-template-columns: 96px repeat(7, minmax(105px, 1fr)); min-height: 38px; padding: 0; color: #fff; }.schedule-shift-heading__name { display: flex; align-items: center; min-width: 0; padding: 6px 10px; }.schedule-shift-heading strong { font-size: 13px; text-transform: uppercase; letter-spacing: .35px; }.schedule-shift-event-cell { display: flex; align-items: center; justify-content: center; min-width: 0; min-height: 38px; padding: 0 5px; border-left: 1px solid rgba(255,255,255,.25); }.schedule-shift-event-cell :deep(.v-field__input) { min-height: 30px; padding: 2px 3px; color: #fff; font-size: 11px; font-weight: 800; text-align: center; }.schedule-shift-event-cell :deep(input) { text-align: center; }.schedule-shift-event-cell :deep(input::placeholder) { color: rgba(255,255,255,.72); opacity: 1; }.schedule-shift-event-value { display: block; width: 100%; overflow: hidden; color: #fff; font-size: 12px; font-weight: 900; text-align: center; text-overflow: ellipsis; text-shadow: 0 1px 1px rgba(0,0,0,.28); white-space: nowrap; }
.schedule-grid { display: grid; grid-template-columns: 96px repeat(7, minmax(105px, 1fr)); }.schedule-grid > div { min-width: 0; border-right: 1px solid #dedede; border-bottom: 1px solid #dedede; }.schedule-grid > div:nth-child(8n) { border-right: 0; }.schedule-grid > div:nth-last-child(-n + 8) { border-bottom: 0; }
.schedule-corner, .schedule-row-label { background: #f6f6f6; color: #606060; font-size: 10px; font-weight: 800; letter-spacing: .6px; padding: 8px; }.schedule-corner { display: flex; align-items: center; justify-content: center; }.schedule-corner-time { color: #5f6d79; font-size: 10px; font-weight: 800; letter-spacing: 0; line-height: 1.1; text-align: center; white-space: nowrap; }.schedule-day-heading { display: flex; align-items: center; justify-content: space-between; padding: 5px 8px; background: #fafafa; color: #4c4c4c; }.schedule-day-heading span { font-size: 10px; font-weight: 800; }.schedule-day-heading strong { font-size: 13px; }.schedule-monitor-label { display: flex; align-items: center; padding: 7px 8px; color: #506c85; background: #e8f0f8; font-size: 10px; font-weight: 800; letter-spacing: .6px; text-transform: uppercase; }.schedule-monitor-cell { background: #e8f0f8; padding: 0 4px; }.schedule-monitor-cell :deep(.v-field__input) { min-height: 28px; padding: 2px 3px; font-size: 13px; font-weight: 900; text-align: center; color: #36617f; }.schedule-monitor-cell :deep(input) { text-align: center; }.schedule-row-label { display: flex; justify-content: space-between; align-items: flex-start; }.schedule-room-label { align-items: center; padding: 4px 8px; }.schedule-room-label strong { color: #385d7a; font-size: 12px; }.schedule-model-cell { min-height: 104px; padding: 5px; background: #fff; cursor: default; }.schedule-room-cell { position: relative; display: flex; align-items: center; min-height: 35px; padding: 3px 4px; }.schedule-model-cell--ready { cursor: copy; background: linear-gradient(135deg, #fff 0%, #fff 72%, #f0f7fc 100%); box-shadow: inset 0 0 0 1px rgba(79,145,205,.18); }.schedule-model-cell--ready:hover { background: #eaf4fb; }.schedule-model-cell--day-off { background: linear-gradient(135deg, #fff 0%, #fff 72%, #eff8f0 100%); box-shadow: inset 0 0 0 1px rgba(79,164,95,.28); cursor: cell; }.schedule-model-cell--day-off:hover { background: #eaf6ec; }.schedule-model-chip { display: inline-flex; align-items: center; max-width: 100%; min-height: 24px; border: 1px solid #d9e2eb; border-radius: 3px; background: #f1f5f9; color: #33485d; font-size: 10px; font-weight: 800; overflow: hidden; }.schedule-model-chip--day-off { border-color: #6eab78; background: #dff1e2; color: #2f713d; }.schedule-model-chip--day-off .schedule-model-chip__label { display: inline-flex; align-items: center; gap: 3px; }.schedule-model-chip--day-off .schedule-model-chip__actions { border-left-color: #b9dbbf; }.schedule-model-chip--day-off .schedule-model-chip__button { color: #397d48; border-right-color: #c7e2cb; }.schedule-model-chip__label { overflow: hidden; padding: 3px 4px 3px 6px; text-overflow: ellipsis; white-space: nowrap; }.schedule-model-chip__actions { display: inline-flex; align-self: stretch; border-left: 1px solid #d5e0e9; }.schedule-model-chip__button { display: inline-flex; align-items: center; justify-content: center; width: 18px; padding: 0; border: 0; border-right: 1px solid #dce5ed; background: transparent; color: #50718d; cursor: pointer; }.schedule-model-chip__button:last-child { border-right: 0; }.schedule-model-chip__button:hover { background: #dcebf6; color: #205b8c; }.schedule-model-chip__button--remove:hover { background: #f8e1e1; color: #b33b3b; }.schedule-model-chip--day-off .schedule-model-chip__button--remove:hover { background: #cbe8d0; color: #276334; }.schedule-apply-hint { display: flex; align-items: center; gap: 3px; color: #47789e; font-size: 10px; font-weight: 750; line-height: 1.1; }.schedule-model-cell--day-off .schedule-apply-hint { color: #42834e; }.schedule-model-cell :deep(.v-field__input) { min-height: 25px; padding: 0 2px; font-size: 11px; }.schedule-model-cell :deep(.v-field__outline) { display: none; }
.schedule-cell-note-marker { position: absolute; top: 0; right: 0; width: 22px; height: 22px; padding: 0; border: 0; background: transparent; cursor: pointer; z-index: 4; }
.schedule-cell-note-marker:hover::before { position: absolute; top: -8px; right: 2px; min-width: 140px; max-width: 220px; padding: 7px 9px; transform: translateY(-100%); border-radius: 4px; background: #1c2321; box-shadow: 0 8px 18px rgba(28,35,33,.18); color: #fff; content: attr(data-comment); font-size: 12px; font-weight: 500; line-height: 1.35; text-align: left; white-space: pre-wrap; z-index: 10; }
.schedule-cell-note-marker::after { position: absolute; top: 0; right: 0; width: 0; height: 0; border-left: 10px solid transparent; border-top: 10px solid #1976d2; content: ''; }
.schedule-note-readonly { min-height: 72px; padding: 11px 12px; border: 1px solid #d4d9de; border-radius: 4px; background: #fafbfc; color: #28333d; font-size: 14px; line-height: 1.45; white-space: pre-wrap; }
.schedule-monitor-value { display: flex; align-items: center; justify-content: center; min-height: 28px; color: #36617f; font-size: 13px; font-weight: 900; text-align: center; }
.schedule-empty-cell .schedule-apply-hint { display: flex; flex: 1; align-self: stretch; align-items: center; justify-content: center; gap: 5px; min-width: 0; padding: 0 7px; border: 0; background: transparent; color: #47789e; font-size: 12px; font-weight: 800; line-height: 1.1; cursor: pointer; }
.schedule-empty-cell .schedule-apply-hint:hover { background: #eaf4fb; }
.schedule-model-cell--editing:not(.schedule-model-cell--ready) { background: #fcfdfe; }
.schedule-model-cell--drag-target { background: #e7f3fc !important; box-shadow: inset 0 0 0 2px #3f84bd !important; }
.schedule-model-chip--editable { display: grid; width: 100%; grid-template-areas: "main edit"; grid-template-columns: minmax(0, 1fr) auto; align-items: center; cursor: pointer; transition: border-color 120ms ease, box-shadow 120ms ease; }
.schedule-model-chip--editable[draggable="true"] { cursor: grab; }
.schedule-model-chip--editable[draggable="true"]:active { cursor: grabbing; }
.schedule-model-chip--editable:hover { border-color: #72a3cc; box-shadow: 0 0 0 2px rgba(79,145,205,.1); }
.schedule-model-chip__mainline { display: flex; min-width: 0; grid-area: main; flex-wrap: wrap; align-items: center; row-gap: 2px; }
.schedule-model-chip--editable .schedule-model-chip__label { flex: 0 1 auto; min-width: 0; max-width: 100%; }
.schedule-model-until { display: inline-flex; flex: 0 0 auto; align-items: center; align-self: center; gap: 3px; margin: 1px 5px; transform: translateY(1px); color: #315f86; font-size: 11.5px; font-weight: 750; line-height: 1.2; white-space: nowrap; }
.schedule-model-until--display { margin: 1px 0 1px 6px; vertical-align: middle; }
.schedule-model-focus-marker { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 3px; margin-right: 5px; color: #2e7d32; font-size: 13px; font-weight: 750; letter-spacing: 0; text-transform: none; white-space: nowrap; }
.schedule-model-focus-marker--display { margin-left: 6px; margin-right: 0; vertical-align: middle; }
.schedule-model-chip__edit-icon { flex: 0 0 auto; grid-area: edit; margin-right: 5px; opacity: .58; }
.schedule-model-text { display: flex; width: 100%; overflow: hidden; flex-direction: column; align-items: center; justify-content: center; padding: 4px 6px; color: #202020; font-size: 13px; font-weight: 750; line-height: 1.2; text-align: center; }
.schedule-model-display-mainline { display: flex; width: 100%; min-width: 0; flex-wrap: wrap; align-items: center; justify-content: center; row-gap: 2px; }
.schedule-model-display-name { overflow: hidden; min-width: 0; max-width: 100%; flex: 0 1 auto; text-overflow: ellipsis; white-space: nowrap; }
.schedule-model-reason { width: 100%; justify-content: center; font-size: 13px; font-weight: 750; text-align: center; }
.schedule-model-reason .schedule-model-chip__label { width: 100%; padding: 3px 6px; justify-content: center; text-align: center; }
.schedule-cell-menu :deep(.v-card-text) { padding: 12px; }
.schedule-work-time-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.schedule-cell-menu :deep(.v-list-subheader) { min-height: 28px; font-size: 10px; font-weight: 850; letter-spacing: .6px; }
.schedule-color-palette { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 5px 13px 12px; }
.schedule-quick-actions { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; padding: 8px; }
.schedule-quick-action { display: flex; min-width: 0; min-height: 48px; flex-direction: column; align-items: center; justify-content: center; gap: 2px; padding: 5px 2px; border: 0; border-radius: 4px; background: transparent; color: #42596d; cursor: pointer; }
.schedule-quick-action span { font-size: 9px; font-weight: 800; line-height: 1; }
.schedule-quick-action:hover { background: #edf4f9; color: #22669d; }
.schedule-quick-action--active { background: #e8f2fa; color: #1976d2; }
.schedule-delete-heading { color: #a63d3d !important; }
.schedule-delete-actions { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; padding: 0 8px 9px; }
.schedule-delete-action { display: flex; min-width: 0; min-height: 48px; flex-direction: column; align-items: center; justify-content: center; gap: 2px; padding: 5px 2px; border: 0; border-radius: 4px; background: transparent; color: #b33d3d; cursor: pointer; }
.schedule-delete-action span { font-size: 9px; font-weight: 800; line-height: 1; }
.schedule-delete-action:hover { background: #faeaea; color: #8f2525; }
.schedule-color-swatch { width: 24px; height: 24px; padding: 0; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 0 0 1px #c7cdd3; cursor: pointer; transition: transform 120ms ease, box-shadow 120ms ease; }
.schedule-color-swatch:hover { transform: scale(1.12); box-shadow: 0 0 0 2px #91acc2; }
.schedule-color-swatch--active { box-shadow: 0 0 0 3px #4f91cd; }
.schedule-cell-menu__remove { color: #b43c3c; }
.schedule-clear-week-btn { color: #aa3e3e; }
.schedule-availability { margin-top: 14px; border: 1px solid #d8d8d8; border-radius: 6px; background: #fff; overflow: hidden; }.schedule-availability-heading { display: flex; justify-content: space-between; align-items: center; padding: 9px 12px; background: #f8f8f8; border-bottom: 1px solid #dedede; }.schedule-availability-heading span { display: block; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .35px; }.schedule-availability-heading small { color: #777; font-size: 11px; }.schedule-off-grid { display: grid; grid-template-columns: repeat(7, minmax(105px, 1fr)); }.schedule-off-day { min-height: 93px; border-right: 1px solid #e1e1e1; padding: 5px; }.schedule-off-day:last-child { border-right: 0; }.schedule-off-day-heading { color: #696969; font-size: 10px; }.schedule-off-chips { display: flex; min-height: 41px; flex-wrap: wrap; align-content: flex-start; gap: 3px; margin-top: 4px; }.schedule-off-chips :deep(.v-chip) { max-width: 100%; font-size: 9px; border-radius: 3px; }.schedule-off-add { display: flex; align-items: center; }.schedule-off-add :deep(.v-field__input) { min-height: 25px; padding: 0 2px; font-size: 10px; }.schedule-off-add :deep(.v-field__outline) { display: none; }
.schedule-dialog-card :deep(.v-card-title) { padding: 20px 24px 4px; font-size: 19px; font-weight: 800; }.schedule-dialog-card :deep(.v-card-text) { padding: 14px 24px; }.schedule-dialog-helper { margin: 0 0 12px; color: #727272; font-size: 13px; }.schedule-creation-options :deep(.v-selection-control) { align-items: flex-start; margin: 7px 0; }.schedule-creation-options strong, .schedule-creation-options small { display: block; }.schedule-creation-options strong { font-size: 14px; }.schedule-creation-options small { margin-top: 2px; color: #707070; font-size: 12px; }.shift-settings-row { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 10px; margin: 11px 0; }
.schedule-room-cell { display: flex; align-items: center; min-height: 35px; padding: 3px 4px; }.schedule-empty-cell { display: flex; align-items: center; width: 100%; min-width: 0; }.schedule-empty-cell :deep(.v-autocomplete) { flex: 1; min-width: 0; }.schedule-empty-cell :deep(.v-field__input) { min-height: 24px; padding: 0 2px; font-size: 10px; }.schedule-empty-cell :deep(.v-btn) { color: #58758e; }.schedule-model-chip--vacation { border-color: #69a89d; background: #d8f0ec; color: #276b62; }.schedule-model-chip--permission { border-color: #d4ad61; background: #fff0cb; color: #8a621c; }.schedule-model-chip--period-day { border-color: #d49aa7; background: #f8e3e8; color: #9a4d61; }.schedule-model-chip--changed-shift { border-color: #dd9d64; background: #ffead8; color: #99531d; }
.schedule-model-chip.schedule-model-reason { border: 0; background: transparent; box-shadow: none; }
.schedule-screenshot-capture.is-capturing { padding: 10px; background: #fff; }.schedule-screenshot-capture.is-capturing .schedule-grid-shell { gap: 8px; }.schedule-screenshot-capture.is-capturing .schedule-shift-heading__action, .schedule-screenshot-capture.is-capturing .schedule-model-chip__actions, .schedule-screenshot-capture.is-capturing .schedule-empty-cell, .schedule-screenshot-capture.is-capturing .schedule-apply-hint { display: none !important; }.schedule-screenshot-capture.is-capturing .schedule-model-cell, .schedule-screenshot-capture.is-capturing .schedule-room-cell { min-height: 33px; padding: 2px 4px; }.schedule-screenshot-capture.is-capturing .schedule-model-cell--ready { cursor: default; background: #fff; box-shadow: none; }.schedule-screenshot-capture.is-capturing .schedule-model-chip { min-height: 22px; }.schedule-screenshot-capture.is-capturing .schedule-model-chip__label { padding: 3px 6px; }.schedule-screenshot-heading { margin: 0 0 12px; color: #173f68; font-size: 27px; font-weight: 850; letter-spacing: .02em; text-align: center; text-transform: uppercase; }
@media (max-width: 980px) { .model-schedule-page { padding: 12px; overflow-x: auto; }.schedule-page-heading { align-items: flex-start; flex-direction: column; }.schedule-controls { grid-template-columns: 1fr; }.schedule-controls-actions { justify-content: flex-start; flex-wrap: wrap; }.model-tray, .schedule-grid-shell, .schedule-availability { min-width: 880px; }.schedule-week-control { justify-content: flex-start; transform: none; }.schedule-week-title { text-align: left; } }
@media print { :global(.control-header), :global(.v-navigation-drawer) { display: none !important; }:global(.v-main) { padding-left: 0 !important; }.model-schedule-page { padding: 0; max-width: none; }.schedule-page-heading, .schedule-page-actions, .schedule-controls, .model-tray, .schedule-empty-cell, .schedule-off-add, .save-status, .schedule-shift-heading__action, .schedule-model-chip__actions { display: none !important; }.schedule-screenshot-heading { display: block; margin: 0 0 12px; color: #173f68; font-size: 27px; font-weight: 850; letter-spacing: .02em; text-align: center; text-transform: uppercase; }.schedule-shift-section, .schedule-availability { break-inside: avoid; }.schedule-grid-shell { gap: 8px; }.schedule-model-cell, .schedule-room-cell { min-height: 33px !important; padding: 2px 4px; }.schedule-model-chip { min-height: 22px; }.schedule-model-chip__label { padding: 3px 6px; }.schedule-monitor-cell :deep(.v-field__input) { min-height: 25px; } }
</style>
